import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { 
  ConnectButton, 
  useCurrentAccount, 
  useSignAndExecuteTransaction,
  useSuiClient,
  useSuiClientQuery
} from '@mysten/dapp-kit';
import { Transaction } from '@mysten/sui/transactions';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DISTIL_PACKAGE_ID,
  DISTIL_REFINERY_ID,
  DISTIL_REFINERY_CONFIG_ID,
  DISTIL_BADGE_REGISTRY_ID,
  DISTIL_COIN_TYPE,
  EVENT_TYPES,
  BADGE_TIERS,
  getTierFromScore,
  formatPurityScore,
  formatSuiAmount,
  getBadgeImageUrl,
} from './constants';
import './App.css';
import Distillery from './components/Distillery';
import DashboardGrid from './components/DashboardGrid';
import LandingPage from './components/LandingPage';
import { categorizeObject, getCategoryBreakdown } from './utils/toxicityCategories';
import { savePurification, clearDemoHistory } from './utils/historyManager';
import { recordPurification as recordToSupabase, recordGameReward, syncWalletPurityScore, getAdminConfig, saveWhitelistToSupabase, getWhitelistFromSupabase, saveQuarantineToSupabase, getQuarantineFromSupabase } from './utils/supabaseManager';
import { trackBurn } from './utils/statsManager';
import { trackPurification as trackPurificationForTGE, getUserStats, trackToiletModeStats, resetUserTracking } from './utils/userTrackingManager';
import BetaTracker from './components/BetaTracker';
import { REWARD_SPLIT } from './constants';
import { calculateBonusReward, getUserDistilBalance, setDemoDistilBalance } from './utils/holdingBonusManager';
import { getBaseRewardRate, trackEmission, resetEmissions } from './utils/emissionSchedule';
import HistoryPanel from './components/HistoryPanel';
import StatsPanel from './components/StatsPanel';
import {
  shouldEnableToiletMode,
  applyToiletTheme,
  removeToiletTheme,
  calculateEnhancedPurity,
  getButtonText,
  triggerFlushEffect,
  updateFlushStreak,
  getFlushStreak,
  TOILET_FEATURES,
  TOILET_TOKEN_THRESHOLD,
  setWheelPrize,
  getWheelPrize,
  clearWheelPrize,
  applyWheelPrize,
} from './utils/toiletMode';
import './components/ToiletMode.css';
import FlushFrenzy from './components/FlushFrenzy';
import ToiletSpinWheel from './components/ToiletSpinWheel';
import ScratchOff from './components/ScratchOff';
import { checkAchievements, syncAchievementsFromSupabase } from './utils/achievementsManager';
import { filterWhitelisted, addToWhitelist, addToWhitelistWithMeta, isWhitelisted, removeFromWhitelist, getWhitelist } from './utils/whitelistManager';
import { initSafeTokens, isSafeObject } from './utils/safeTokens';
import { filterObjectsForCleanup } from './utils/dustDetection';
import { enrichObjectsWithMetadata, getObjectDisplayInfo, extractCoinType, verifyDecimalsInBackground, KNOWN_ICONS } from './utils/coinMetadata';
import { calculateUSDValue, getValueTier, getValueLabel, formatUSDValue } from './utils/priceApi';
import { fetchTokenData, fetchPricesForTokens, clearPriceCache, getCachedPrice } from './utils/geckoApi';
import PurifyConfirmationModal from './components/PurifyConfirmationModal';
import './components/PurifyConfirmationModal.css';
import MilestoneAlert from './components/MilestoneAlert';
import './components/MilestoneAlert.css';
import { generateTierUpgradeTweet, shareOnTwitter, isPurificationMilestone, generatePurificationMilestoneTweet } from './utils/shareUtils';
import LoadingSpinner from './components/LoadingSpinner';
import './components/LoadingSpinner.css';
import ErrorModal from './components/ErrorModal';

// 🚀 BETA LAUNCH IMPORTS
import { 
  isTrackingEnabled, 
  getCurrentMultiplier, 
  getBetaStatus,
  BETA_LAUNCH_CONFIG 
} from './betaConfig';
import { updateStreak, resetStreak, syncStreakFromSupabase } from './utils/dailyStreaksManager';
import {
  getDailyRemaining,
  getDailyLimit,
  getDailyUsed,
  incrementDailyCount,
  hasReachedLimit,
  getResetCountdown,
  resetDailyCount,
  saveToiletHolderStatus,
} from './dailyLimitsManager';
import DailyLimitIndicator from './DailyLimitIndicator';
import './components/ErrorModal.css';
import AdminPanel from './components/AdminPanel';
import './components/AdminPanel.css';
import ProtectedAssetsModal from './components/ProtectedAssetsModal';
import QuarantineModal from './components/QuarantineModal';
import Whitepaper from './Whitepaper';
import ReferralModal from './ReferralModal';
import ReferralWelcomeBanner from './ReferralWelcomeBanner';
import { captureReferralFromUrl, onReferralWalletConnect, checkReferralCompletion } from './utils/referralManager';
import './referral.css';

// 🔐 ADMIN ACCESS - Replace with your wallet address
const ADMIN_WALLET = '0x9e36a67e9c8ea5db13ca8d35df09d678808fbed86dbfa4c27e957fb44b90479f';
import { 
  checkPurificationMilestone, 
  checkObjectsMilestone, 
  checkBurnMilestone,
  checkPurityMilestone,
  formatMilestoneToast 
} from './utils/achievementNotifications';
import WhitelistPanel from './components/WhitelistPanel';
import ToiletWarriorActivation from './components/ToiletWarriorActivation';
import HelpGuide from './HelpGuide';
import FirstPurgeWarningModal from './FirstPurgeWarningModal';

// Degen success messages for variety
const DEGEN_SUCCESS_MESSAGES = [
  '💎 BOOM! TRASH OBLITERATED!',
  '🔥 WALLET STATUS: PRISTINE!',
  '⚡ GET PURIFIED, PEASANT!',
  '👑 CLEANER THAN YOUR EX!',
  '💰 TRASH OUT, GAINS IN!',
  '🚀 NGMI? NOT YOU!',
  '✨ IMMACULATE VIBES ONLY!',
  '💪 WALLET GAINS +1000 AURA!',
  '🎯 SCAMMERS = ZERO. YOU = HERO!',
  '🧹 SWEPT CLEANER THAN A DAO!',
  '🏆 ABSOLUTE DOMINATION MODE!',
  '⚔️ SPAM DELETED. MISSION ACCOMPLISHED!',
  '🎰 JACKPOT! WALLET = CLEAN AF!',
  '🌊 PURIFIED LIKE A TSUNAMI!',
  '🗑️ GARBAGE DUMPED. GAINS SECURED!',
  '💥 POW! SCAMMERS REKT!',
  '🎪 TRASH CIRCUS = SHUT DOWN!',
  '🔮 CRYSTAL CLEAN WALLET ENERGY!',
  '🎨 WALLET LOOKIN\' FRESH!',
  '⚡ ZAP! FILTH ERADICATED!',
];

// ═══════════════════════════════════════════════════════════════════════════
// DEMO OBJECTS
// 15 fake spam tokens (enriched.isCoin=true so grouping works) + 5 fake NFTs
// Nothing is calculated or saved in demo mode — pure look-around experience.
// ═══════════════════════════════════════════════════════════════════════════

const DEMO_OBJECTS = [

  // ══════════════════════════════════════════════════════════════════
  // TIER 1 — HIGH VALUE ($50–$500)
  // These trigger the high-value warning + "type PURIFY to confirm" flow
  // Great for showing users WHY the security features exist
  // ══════════════════════════════════════════════════════════════════
  {
    // $247 — should make user think twice and read the warning carefully
    data: { objectId: 'demo_token_001', type: '0x0000001::coin::Coin<0x0000001::legit_project::LEGIT>', display: { data: { name: 'LegitProject', description: '✅ Looks legit — but is it? Whitelist me if unsure!' } } },
    enriched: { isCoin: true, coinType: '0x0000001::legit_project::LEGIT', symbol: 'LEGIT', displayName: 'LegitProject', rawBalance: '247000000000000', iconUrl: null },
    balance: 247000, mockUsdValue: 247.00,
  },
  {
    // $489 — highest value, definitely triggers the danger zone modal
    data: { objectId: 'demo_token_002', type: '0x0000002::coin::Coin<0x0000002::whale_bag::WHALE>', display: { data: { name: 'WhaleBag', description: '🐋 Big bag — use whitelist to protect, or purge if spam' } } },
    enriched: { isCoin: true, coinType: '0x0000002::whale_bag::WHALE', symbol: 'WHALE', displayName: 'WhaleBag', rawBalance: '489000000000000', iconUrl: null },
    balance: 489000, mockUsdValue: 489.00,
  },
  {
    // $85 — mid-high, shows the standard high-value warning
    data: { objectId: 'demo_token_003', type: '0x0000003::coin::Coin<0x0000003::airdrop_reward::AIRDROP>', display: { data: { name: 'AirdropReward2024', description: '🎁 Unsolicited airdrop — could be valuable or a trap' } } },
    enriched: { isCoin: true, coinType: '0x0000003::airdrop_reward::AIRDROP', symbol: 'AIRDP', displayName: 'AirdropReward2024', rawBalance: '85000000000000', iconUrl: null },
    balance: 85000, mockUsdValue: 85.00,
  },

  // ══════════════════════════════════════════════════════════════════
  // TIER 2 — MEDIUM VALUE ($1–$20)
  // Standard confirmation modal territory — shows the review flow
  // ══════════════════════════════════════════════════════════════════
  {
    // $12.50 — typical medium value spam
    data: { objectId: 'demo_token_004', type: '0x0000004::coin::Coin<0x0000004::moon_inu::MINU>', display: { data: { name: 'MoonInu', description: "🐕 Dog coin to the moon (it wasn't)" } } },
    enriched: { isCoin: true, coinType: '0x0000004::moon_inu::MINU', symbol: 'MINU', displayName: 'MoonInu', rawBalance: '12500000000000', iconUrl: null },
    balance: 12500, mockUsdValue: 12.50,
  },
  {
    // $7.80 — honeypot, looks appealing but worthless
    data: { objectId: 'demo_token_005', type: '0x0000005::coin::Coin<0x0000005::honeypot_gold::HPOT>', display: { data: { name: 'HoneypotGold', description: "🍯 Can buy, can't sell — classic trap" } } },
    enriched: { isCoin: true, coinType: '0x0000005::honeypot_gold::HPOT', symbol: 'HPOT', displayName: 'HoneypotGold', rawBalance: '7800000000000', iconUrl: null },
    balance: 7800, mockUsdValue: 7.80,
  },
  {
    // $3.20 — reflection scam
    data: { objectId: 'demo_token_006', type: '0x0000006::coin::Coin<0x0000006::reflection_fi::RFLCT>', display: { data: { name: 'ReflectionFi', description: '♻️ Reflections that never actually reflect' } } },
    enriched: { isCoin: true, coinType: '0x0000006::reflection_fi::RFLCT', symbol: 'RFLCT', displayName: 'ReflectionFi', rawBalance: '3200000000000', iconUrl: null },
    balance: 3200, mockUsdValue: 3.20,
  },
  {
    // $1.05 — just above dust, borderline case
    data: { objectId: 'demo_token_007', type: '0x0000007::coin::Coin<0x0000007::pump_rocket::PRKT>', display: { data: { name: 'PumpRocket', description: '🚀 Pumped and dumped in 3 hours flat' } } },
    enriched: { isCoin: true, coinType: '0x0000007::pump_rocket::PRKT', symbol: 'PRKT', displayName: 'PumpRocket', rawBalance: '1050000000000', iconUrl: null },
    balance: 1050, mockUsdValue: 1.05,
  },

  // ══════════════════════════════════════════════════════════════════
  // TIER 3 — WHITELIST CANDIDATES
  // Named to look legitimate — teaches users to whitelist before purging
  // ══════════════════════════════════════════════════════════════════
  {
    // $18 — named like a real project, good whitelist teaching moment
    data: { objectId: 'demo_token_008', type: '0x0000008::coin::Coin<0x0000008::sui_defi::SDEFI>', display: { data: { name: 'SuiDeFi Protocol', description: '🔒 Looks like a real project — whitelist me before purging!' } } },
    enriched: { isCoin: true, coinType: '0x0000008::sui_defi::SDEFI', symbol: 'SDEFI', displayName: 'SuiDeFi Protocol', rawBalance: '18000000000000', iconUrl: null },
    balance: 18000, mockUsdValue: 18.00,
  },
  {
    // $5.50 — another whitelist candidate
    data: { objectId: 'demo_token_009', type: '0x0000009::coin::Coin<0x0000009::safe_gains::SGAIN>', display: { data: { name: 'SafeGains', description: '🛡️ Suspicious name — whitelist if you own this project' } } },
    enriched: { isCoin: true, coinType: '0x0000009::safe_gains::SGAIN', symbol: 'SGAIN', displayName: 'SafeGains', rawBalance: '5500000000000', iconUrl: null },
    balance: 5500, mockUsdValue: 5.50,
  },

  // ══════════════════════════════════════════════════════════════════
  // TIER 4 — DUST / ZERO VALUE
  // Classic spam dust — fast purge, no confirmation needed
  // ══════════════════════════════════════════════════════════════════
  {
    data: { objectId: 'demo_token_010', type: '0x000000a::coin::Coin<0x000000a::claim_prize::CLPRZ>', display: { data: { name: 'ClaimPrize', description: '🎣 Phishing token — do not click any links' } } },
    enriched: { isCoin: true, coinType: '0x000000a::claim_prize::CLPRZ', symbol: 'CLPRZ', displayName: 'ClaimPrize', rawBalance: '1000000000', iconUrl: null },
    balance: 1, mockUsdValue: 0.00,
  },
  {
    data: { objectId: 'demo_token_011', type: '0x000000b::coin::Coin<0x000000b::dust_token::DUST>', display: { data: { name: 'DustToken', description: '💨 Classic spam dust attack residue' } } },
    enriched: { isCoin: true, coinType: '0x000000b::dust_token::DUST', symbol: 'DUST', displayName: 'DustToken', rawBalance: '1000', iconUrl: null },
    balance: 0.000001, mockUsdValue: 0.00,
  },
  {
    data: { objectId: 'demo_token_012', type: '0x000000c::coin::Coin<0x000000c::ponzi_protocol::PONZI>', display: { data: { name: 'PonziProtocol', description: '🎰 1000% APY (lasted about 48 hours)' } } },
    enriched: { isCoin: true, coinType: '0x000000c::ponzi_protocol::PONZI', symbol: 'PONZI', displayName: 'PonziProtocol', rawBalance: '3333000000000', iconUrl: null },
    balance: 3333, mockUsdValue: 0.00,
  },
  {
    data: { objectId: 'demo_token_013', type: '0x000000d::coin::Coin<0x000000d::exit_scam::EXIT>', display: { data: { name: 'ExitScam', description: '🏃 Dev vanished with the liquidity at 3am' } } },
    enriched: { isCoin: true, coinType: '0x000000d::exit_scam::EXIT', symbol: 'EXIT', displayName: 'ExitScam', rawBalance: '5000000000000', iconUrl: null },
    balance: 5000, mockUsdValue: 0.00,
  },
  {
    data: { objectId: 'demo_token_014', type: '0x000000e::coin::Coin<0x000000e::rug_dao::RUGDAO>', display: { data: { name: 'RugDAO', description: '📉 Governance token, protocol never existed' } } },
    enriched: { isCoin: true, coinType: '0x000000e::rug_dao::RUGDAO', symbol: 'RUGDAO', displayName: 'RugDAO', rawBalance: '420000000000', iconUrl: null },
    balance: 420, mockUsdValue: 0.00,
  },
  {
    data: { objectId: 'demo_token_015', type: '0x000000f::coin::Coin<0x000000f::ghost_token::GHST>', display: { data: { name: 'GhostToken', description: '👻 Team disappeared 2 days after mint' } } },
    enriched: { isCoin: true, coinType: '0x000000f::ghost_token::GHST', symbol: 'GHST', displayName: 'GhostToken', rawBalance: '666000000000', iconUrl: null },
    balance: 666, mockUsdValue: 0.00,
  },

  // ── FAKE NFTs (5) — shown with NFT.png + "NFT vault coming in V2" notice ─
  {
    data: { objectId: 'demo_nft_001', type: '0x0000010::spam_nft::SpamNFT', display: { data: { name: 'FREE_AIRDROP_NFT', description: '🎣 Phishing attempt NFT', image_url: null } } },
    enriched: { isCoin: false, isNFT: true, displayName: 'FREE_AIRDROP_NFT', iconUrl: null },
    balance: 0, mockUsdValue: 0.00, isNFT: true,
  },
  {
    data: { objectId: 'demo_nft_002', type: '0x0000011::dead_nft::DeadNFT', display: { data: { name: 'BoredApeKnockoff #4201', description: '🪦 Floor price: 0 SUI', image_url: null } } },
    enriched: { isCoin: false, isNFT: true, displayName: 'BoredApeKnockoff #4201', iconUrl: null },
    balance: 0, mockUsdValue: 0.00, isNFT: true,
  },
  {
    data: { objectId: 'demo_nft_003', type: '0x0000012::scam_nft::ScamNFT', display: { data: { name: 'CLAIM_YOUR_PRIZE_NOW', description: '☠️ Malicious NFT — do not interact', image_url: null } } },
    enriched: { isCoin: false, isNFT: true, displayName: 'CLAIM_YOUR_PRIZE_NOW', iconUrl: null },
    balance: 0, mockUsdValue: 0.00, isNFT: true,
  },
  {
    data: { objectId: 'demo_nft_004', type: '0x0000013::vaporware_nft::VaporwareNFT', display: { data: { name: 'MetaGameItem #0099', description: "🎮 Game launched in 2022 (it didn't)", image_url: null } } },
    enriched: { isCoin: false, isNFT: true, displayName: 'MetaGameItem #0099', iconUrl: null },
    balance: 0, mockUsdValue: 0.00, isNFT: true,
  },
  {
    data: { objectId: 'demo_nft_005', type: '0x0000014::bot_nft::BotNFT', display: { data: { name: 'GenericMonkeyJPEG #7777', description: '🙈 Auto-generated spam collection', image_url: null } } },
    enriched: { isCoin: false, isNFT: true, displayName: 'GenericMonkeyJPEG #7777', iconUrl: null },
    balance: 0, mockUsdValue: 0.00, isNFT: true,
  },
];



// ═══════════════════════════════════════════════════════════════════════════
// OBJECT VALUE DISPLAY COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

function ObjectValueDisplay({ objectType, balance, decimals = 9, mockUsdValue, cachedPrice }) {
  const [usdValue, setUsdValue] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    let isMounted = true;
    
    // If mock value provided (demo mode), use it immediately
    if (mockUsdValue !== undefined && mockUsdValue !== null) {
      setUsdValue(mockUsdValue);
      setLoading(false);
      return;
    }
    
    // ✅ USE CACHED PRICE if available (instant display!)
    if (cachedPrice !== undefined && cachedPrice !== null) {
      if (isMounted) {
        const value = (balance != null && balance !== '')
          ? calculateUSDValue(balance, decimals, cachedPrice)
          : null;
        setUsdValue(value);
        setLoading(false);
      }
      return;
    }
    
    // Fallback: check DexScreener cache one more time, then give up
    const cachedFallback = getCachedPrice(objectType);
    if (isMounted && cachedFallback && balance) {
      const value = calculateUSDValue(balance, decimals, cachedFallback);
      setUsdValue(value);
    } else {
      setUsdValue(null);
    }
    setLoading(false);
    
    return () => {
      isMounted = false;
    };
  }, [objectType, balance, decimals, mockUsdValue, cachedPrice]);
  
  if (loading) {
    return (
      <div className="object-value-loading">
        <span className="value-loading-spinner">⏳</span>
      </div>
    );
  }
  
  // 🔍 ALWAYS show something - either value or unavailable
  if (usdValue === null) {
    return (
      <div className="object-value-display">
        <div className="usd-value usd-value-unknown">
          Price unavailable
        </div>
      </div>
    );
  }
  
  const tier = getValueTier(usdValue);
  const label = getValueLabel(usdValue);
  const formatted = formatUSDValue(usdValue);
  
  return (
    <div className="object-value-display">
      <div className={`usd-value usd-value-${tier}`}>
        {formatted}
      </div>
      {label && (
        <div className={`value-label value-label-${tier}`}>
          {label}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// WARNING BANNER COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

function WarningBanner({ onDismiss }) {
  return (
    <motion.div 
      className="warning-banner"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
    >
      <div className="warning-banner-content">
        <div className="warning-banner-icon">⚠️</div>
        <div className="warning-banner-text">
          <strong>Review Before Purifying</strong>
          <span>High-value items are auto-protected. Check USD values and use whitelist for additional safety.</span>
        </div>
        <button 
          className="warning-banner-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss warning"
        >
          ×
        </button>
      </div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN APP COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

function App() {
  const currentAccount = useCurrentAccount();
  const suiClient = useSuiClient();
  const { mutate: signAndExecuteTransaction } = useSignAndExecuteTransaction();

  
  // State
  const [showLanding, setShowLanding] = useState(() => {
    // ✅ FIX: Use sessionStorage so landing shows on new browser session
    return sessionStorage.getItem('distil_entered_app') !== 'true';
  });
  const [purityScore, setPurityScore] = useState(0);
  const [currentTier, setCurrentTier] = useState(0);
  const [distillationsCount, setDistillationsCount] = useState(0);
  const [isDistilling, setIsDistilling] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [userObjects, setUserObjects] = useState([]);
  const [selectedObjects, setSelectedObjects] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // Track which groups are expanded
  const [hasBadge, setHasBadge] = useState(false);
  const [showFlushFrenzy, setShowFlushFrenzy] = useState(false);
  const [showScratchOff, setShowScratchOff] = useState(false);
  
  // ✅ PRICE CACHE - for instant USD value display
  const [tokenPrices, setTokenPrices] = useState({}); // { tokenType: price }
  const [pricesLoading, setPricesLoading] = useState(false);
  
  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [showLanding]);

  // Capture referral code from URL on mount (?ref=CODE)
  useEffect(() => {
    captureReferralFromUrl();
  }, []);
  
  // Demo Mode State
  const [isDemoMode, setIsDemoMode] = useState(false);

  // Load persisted purity score when wallet connects
  // Fix 4: Supabase is source of truth — sync on connect, use higher of local vs remote
  useEffect(() => {
    if (currentAccount?.address && !isDemoMode) {
      const key = `distil-purity-score-${currentAccount.address}`;
      const localScore = parseInt(localStorage.getItem(key) || '0', 10);
      // Load local immediately so UI isn't blank while we fetch
      if (localScore > 0) setPurityScore(localScore);
      // Then reconcile with Supabase — remote wins if higher (e.g. browser cleared)
      syncWalletPurityScore(currentAccount.address).then(remoteScore => {
        if (remoteScore !== null && remoteScore > localScore) {
          setPurityScore(remoteScore);
          localStorage.setItem(key, remoteScore.toString());
        }
      }).catch(() => {});
      // Restore streak + achievements from Supabase (handles cleared localStorage)
      syncStreakFromSupabase(currentAccount.address).catch(() => {});
      syncAchievementsFromSupabase(currentAccount.address).catch(() => {});
      // Restore whitelist from Supabase — only use remote if local is completely empty
      // handleUnwhitelist now awaits every save, so local is always the true current state
      // Remote is only a fallback for fresh devices that have never had a whitelist
      getWhitelistFromSupabase(currentAccount.address).then(remoteWhitelist => {
        if (remoteWhitelist && Object.keys(remoteWhitelist).length > 0) {
          try {
            const localRaw = localStorage.getItem('objectWhitelist');
            const localWhitelist = localRaw ? JSON.parse(localRaw) : {};
            const localIsEmpty = Object.keys(localWhitelist).length === 0;
            if (localIsEmpty) {
              // Fresh device with no local data — restore from Supabase
              localStorage.setItem('objectWhitelist', JSON.stringify(remoteWhitelist));
            }
            // If local has any data at all, trust it completely — don't merge
            // This prevents reconnect from restoring items the user intentionally removed
          } catch (e) {
            localStorage.setItem('objectWhitelist', JSON.stringify(remoteWhitelist));
          }
        }
      }).catch(() => {});
      // Restore quarantine from Supabase — remote is source of truth (no local fallback)
      // Quarantine is never stored in localStorage, Supabase is always authoritative
      getQuarantineFromSupabase(currentAccount.address).then(remoteQuarantine => {
        if (remoteQuarantine && remoteQuarantine.length > 0) {
          // Populate the session ref FIRST so processObjects excludes these immediately
          remoteQuarantine.forEach(obj => {
            const id = obj?.data?.objectId;
            if (id) sessionQuarantinedIds.current.add(id);
          });
          setQuarantinedObjects(remoteQuarantine);
        }
      }).catch(() => {});
      // Referral: check for pending referral code in localStorage
      onReferralWalletConnect(currentAccount.address).then(result => {
        if (result.showWelcome && result.referrerAddress) {
          setReferralWelcome({ referrerAddress: result.referrerAddress });
        }
      }).catch(() => {});
    } else if (!currentAccount?.address) {
      setPurityScore(0);
    }
  }, [currentAccount?.address, isDemoMode]);
  const [demoDistillations, setDemoDistillations] = useState(0);
  
  // Toilet Mode State
  const [isToiletHolder, setIsToiletHolder] = useState(false);
  const [toiletBalance, setToiletBalance] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [isDemoToiletMode, setIsDemoToiletMode] = useState(() => {
    const saved = localStorage.getItem('distil_demo_toilet_mode');
    return saved === 'true';
  });
  const [flushStreak, setFlushStreak] = useState(0);
  const [showSpinWheel, setShowSpinWheel] = useState(false);
  const [wheelPrize, setWheelPrizeState] = useState(null);
  const [isWheelAvailable, setIsWheelAvailable] = useState(false);
  
  // Safety Features
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showFirstPurgeWarning, setShowFirstPurgeWarning] = useState(false);
  const [pendingPurification, setPendingPurification] = useState(null);
  const [showWhitelist, setShowWhitelist] = useState(false);
  // C2 FIX: default false — set via useEffect after checking wallet address
  const [isAdmin, setIsAdmin] = useState(false);
  // Protocol config loaded from Supabase admin_config table
  const [protocolConfig, setProtocolConfig] = useState({});

  const [showWarriorActivation, setShowWarriorActivation] = useState(false);
  const [showToiletPremiumModal, setShowToiletPremiumModal] = useState(false);
  
  // Tutorial / Docs
  const [showTutorial, setShowTutorial] = useState(false);
  const [showWhitepaper, setShowWhitepaper] = useState(false);
  // Referral
  const [showReferral, setShowReferral] = useState(false);
  const [referralWelcome, setReferralWelcome] = useState(null); // { referrerAddress } | null
  
  // 🎯 Admin Panel
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  
  // 🛡️ Protected Assets Modal
  const [showProtectedModal, setShowProtectedModal] = useState(false);
  const [protectedAssets, setProtectedAssets] = useState([]);
  const [quarantinedObjects, setQuarantinedObjects] = useState([]); // 🚨 Malicious objects
  const [showQuarantineModal, setShowQuarantineModal] = useState(false);
  
  // 🏆⏳🚨 POLISH PACKAGE: Loading, Errors, Achievements
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [lastPurificationCount, setLastPurificationCount] = useState(0);
  const [lastObjectsCount, setLastObjectsCount] = useState(0);
  
  // Warning Banner
  const [warningBannerDismissed, setWarningBannerDismissed] = useState(() => {
    return localStorage.getItem('distil_warning_banner_dismissed') === 'true';
  });
  
  // Mini-Games State

  // ═══════════════════════════════════════════════════════════════════════════
  // SAFE TOKENS INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Initialize safe tokens system on mount
  useEffect(() => {
    initSafeTokens();
    
    // Load wheel prize if exists
    const storedPrize = getWheelPrize();
    if (storedPrize) {
      setWheelPrizeState(storedPrize);
    }
    
    // Load flush streak
    const streak = getFlushStreak();
    setFlushStreak(streak);

    // Load protocol config from Supabase (contract addresses, etc.)
    getAdminConfig().then(config => {
      setProtocolConfig(config);
    }).catch(() => {});
  }, []);

  // C2 FIX: Set isAdmin only after verifying wallet address
  useEffect(() => {
    if (currentAccount?.address) {
      setIsAdmin(currentAccount.address === ADMIN_WALLET);
    } else {
      setIsAdmin(false);
    }
  }, [currentAccount?.address]);

  // Wheel available = just needs to be a toilet holder or demo
  // SpinWheel component handles daily spin counting + bonus spin logic internally
  useEffect(() => {
    setIsWheelAvailable(isToiletHolder || isDemoToiletMode);
  }, [isToiletHolder, isDemoToiletMode]);

  // ✅ FIX: Persist toilet mode changes to localStorage
  useEffect(() => {
    // Only save demo toilet mode preference, not the holder status
    localStorage.setItem('distil_demo_toilet_mode', isDemoToiletMode.toString());
    
    // Apply/remove theme based on toilet mode state
    if (isToiletHolder) {
      applyToiletTheme();
    } else {
      removeToiletTheme();
    }
  }, [isToiletHolder, isDemoToiletMode]);

  // ✅ FIX: Apply toilet theme on mount if user had it enabled
  useEffect(() => {
    const savedToiletMode = localStorage.getItem('distil_toilet_mode');
    if (savedToiletMode === 'true') {
      applyToiletTheme();
    }
  }, []);

  // Fetch user's badge data
  const { data: badgeData, refetch: refetchBadge } = useSuiClientQuery(
    'getDynamicFieldObject',
    {
      parentId: DISTIL_BADGE_REGISTRY_ID,
      name: {
        type: 'address',
        value: currentAccount?.address || '',
      },
    },
    {
      enabled: !!currentAccount?.address && !isDemoMode,
    }
  );

  // Fetch user's owned objects
  const { data: ownedObjects, refetch: refetchObjects } = useSuiClientQuery(
    'getOwnedObjects',
    {
      owner: currentAccount?.address || '',
      options: {
        showType: true,
        showContent: true,
        showDisplay: true,
      },
      limit: 50,  // 🔥 IMPORTANT: Sui max is 50 per page, we'll paginate below
    },
    {
      enabled: !!currentAccount?.address && !isDemoMode,
    }
  );

  // 🔥 NEW: Fetch ALL objects with pagination (Sui limits to 50 per request)
  const [allOwnedObjects, setAllOwnedObjects] = useState(null);
  
  // 🎯 Ellipsis menu state
  const [openMenuId, setOpenMenuId] = useState(null);
  
  // Close ellipsis menu on scroll
   useEffect(() => {
       const handleScroll = () => setOpenMenuId(null);
       window.addEventListener('scroll', handleScroll, true);
       return () => window.removeEventListener('scroll', handleScroll, true);
       }, []);

useEffect(() => {
  if (!currentAccount?.address || isDemoMode || !suiClient) return;
    
    // Capture suiClient in local var — prevents re-running if SDK updates reference
    const client = suiClient;

    async function fetchAllObjects() {
      
      let allObjects = [];
      let hasNextPage = true;
      let cursor = null;
      let pageCount = 0;
      
      try {
        while (hasNextPage) {
          pageCount++;
          
          const response = await client.getOwnedObjects({
            owner: currentAccount.address,
            options: {
              showType: true,
              showContent: true,
              showDisplay: true,
              showOwner: true,              // NEW: Ownership data
              showStorageRebate: true,      // NEW: Critical for rebate calculation!
              showPreviousTransaction: true // NEW: Useful for debugging
            },
            limit: 50,
            cursor: cursor,
          });
          
          allObjects = allObjects.concat(response.data);
          hasNextPage = response.hasNextPage;
          cursor = response.nextCursor;
          
          
          // Safety limit: Stop after 20 pages (1000 objects)
          if (pageCount >= 20) {
            break;
          }
        }
 
        
        setAllOwnedObjects({ data: allObjects });
      } catch (error) {
        console.error('❌ Error fetching all objects:', error);
        // Fallback to first page only
        setAllOwnedObjects(ownedObjects);
      }
    }
    
    fetchAllObjects();
  }, [currentAccount?.address, isDemoMode]); // suiClient intentionally omitted — captured in effect body

  // Update badge data when fetched
  useEffect(() => {
    if (isDemoMode) return;
    
    if (badgeData?.data?.content?.fields) {
      const fields = badgeData.data.content.fields;
      setHasBadge(true);
      const onChainScore = Number(fields.purity_score || 0);
      // Use localStorage score if on-chain is 0 (beta: badge contract not writing score yet)
      const localKey = `distil-purity-score-${currentAccount?.address}`;
      const localScore = parseInt(localStorage.getItem(localKey) || '0', 10);
      const finalScore = onChainScore > 0 ? onChainScore : localScore;
      setPurityScore(finalScore);
      // Always derive tier from score using 8-tier UI system (not on-chain 4-tier)
      setCurrentTier(getTierFromScore(finalScore));
      setDistillationsCount(Number(fields.distillations_count || 0));
    } else {
      setHasBadge(false);
      // Don't reset to 0 - keep localStorage score if exists
      const localKey = `distil-purity-score-${currentAccount?.address}`;
      const localScore = parseInt(localStorage.getItem(localKey) || '0', 10);
      setPurityScore(localScore);
      setCurrentTier(getTierFromScore(localScore));
      setDistillationsCount(0);
    }
  }, [badgeData, isDemoMode]);

  // 🔧 AUTO-EXIT DEMO MODE when wallet connects
  useEffect(() => {
    if (isDemoMode && currentAccount?.address) {
      setIsDemoMode(false);
      setDemoDistillations(0);
      setIsDemoToiletMode(false);
      setDemoDistilBalance(0);
      
      // 🧹 PURGE all demo-polluted localStorage tracking keys
      try {
        clearDemoHistory();        // demo purification history
        resetUserTracking();       // distil_user_tracking
        resetEmissions();          // total_distil_emitted, emission_history
        resetStreak();             // distil_daily_streak
        localStorage.removeItem('total_distil_burned');
        localStorage.removeItem('burn_history');
        localStorage.removeItem('distil_burned_weekly');
        localStorage.removeItem('distil_burned_monthly');
      } catch (e) {
        console.warn('Error clearing demo data:', e);
      }
      
      // Clear demo mode styling
      document.body.classList.remove('toilet-mode');
      removeToiletTheme();
    }
  }, [currentAccount?.address, isDemoMode]);

  // ── PWA Install Prompt ───────────────────────────────────────────────────
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBtn, setShowInstallBtn] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault(); // Stop Chrome showing mini-infobar
      setInstallPrompt(e);
      setShowInstallBtn(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    // Hide button if already installed
    window.addEventListener('appinstalled', () => {
      setShowInstallBtn(false);
      setInstallPrompt(null);
    });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowInstallBtn(false);
      setInstallPrompt(null);
    }
  };

  // ── Catch wallet rejections that bypass onError ──────────────────────────
  // Slush resolves signTransaction as rejected BEFORE our onError fires
  // Use a ref so the handler always has access to current selectedObjects
  const selectedObjectsRef = useRef([]);

  // ── Session-level exclusion guards ───────────────────────────────────────
  // These refs persist for the lifetime of the page and tell processObjects
  // which objectIds to exclude from cleanup list / protected assets on every
  // re-render triggered by allOwnedObjects changes or decimal corrections.
  // Without these, chain-fetched objects re-appear after quarantine/unwhitelist.
  const sessionQuarantinedIds = useRef(new Set());      // IDs quarantined this session
  const sessionRemovedFromWhitelist = useRef(new Set()); // IDs removed from whitelist this session
  const decimalsVerifiedRef = useRef(false);             // Decimal correction runs once per session only
  const recentlyWhitelistedRef = useRef({});             // Cache of objects at whitelist-time for immediate WhitelistPanel display
  useEffect(() => {
    selectedObjectsRef.current = selectedObjects;
  }, [selectedObjects]);

  useEffect(() => {
    const handleRejection = (event) => {
      const msg = (event.reason?.message || event.reason?.toString() || '').toLowerCase();
      console.warn('[Distil] Unhandled rejection caught:', msg);
      const isWalletBlock = (
        msg.includes('user rejected') ||
        msg.includes('user rejected the request') ||
        msg.includes('trpcclienterror') ||
        msg.includes('malicious') ||
        msg.includes('halted')
      );
      if (isWalletBlock) {
        event.preventDefault();
        const currentSelected = selectedObjectsRef.current;
        if (currentSelected.length > 0) {
          // Register in session ref immediately so processObjects excludes them
          currentSelected.forEach(id => sessionQuarantinedIds.current.add(id));
          // Use setState callbacks to avoid stale closure on quarantineObjects
          setUserObjects(prev => {
            const toQuarantine = prev.filter(obj =>
              currentSelected.includes(obj?.data?.objectId)
            );
            const remaining = prev.filter(obj =>
              !currentSelected.includes(obj?.data?.objectId)
            );
            const flagged = toQuarantine.map(obj => ({
              ...obj,
              isQuarantined: true,
              quarantinedAt: Date.now(),
              filterCategory: 'quarantined',
            }));
            setQuarantinedObjects(q => {
              const existingIds = new Set(q.map(o => o?.data?.objectId));
              const newItems = flagged.filter(o => !existingIds.has(o?.data?.objectId));
              const updated = [...q, ...newItems];
              // Persist to Supabase — same as quarantineObjects()
              if (currentAccount?.address) {
                saveQuarantineToSupabase(currentAccount.address, updated).catch(() => {});
              }
              return updated;
            });
            return remaining;
          });
          setSelectedObjects([]);
          setIsLoading(false);
          setIsDistilling(false);
          setShowQuarantineModal(true);
          showToast('⚠️ Transaction blocked — objects moved to Quarantine', 'error');
        }
      }
    };
    window.addEventListener('unhandledrejection', handleRejection);
    return () => window.removeEventListener('unhandledrejection', handleRejection);
  }, []);

  // ── Dynamic theme-color for Samsung nav bar ──────────────────────────────
  // Updates <meta name="theme-color"> so the browser nav bar matches the mode
  useEffect(() => {
    const isToilet = isToiletHolder || isDemoToiletMode;
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = isToilet ? '#020617' : '#22d3ee';
  }, [isToiletHolder, isDemoToiletMode]);

  // ── Ensure toilet-bg-normal is always paired with toilet-mode ─────────────
  // Fixes: landing page nav → back to app shows gold scheme instead of dark cosmic
  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (document.body.classList.contains('toilet-mode') &&
          !document.body.classList.contains('toilet-bg-normal')) {
        document.body.classList.add('toilet-bg-normal');
      }
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Check for $TOILET token holdings
  useEffect(() => {
    const checkToiletHoldings = async () => {
      
      if (!currentAccount?.address || isDemoMode) {
        const shouldEnable = shouldEnableToiletMode(0, isDemoToiletMode);
        setIsToiletHolder(shouldEnable);
        saveToiletHolderStatus(shouldEnable);
        setToiletBalance(0);
        
        
        if (shouldEnable) {
          applyToiletTheme();
          const streakData = getFlushStreak();
          setFlushStreak(streakData.count);
        } else {
          removeToiletTheme();
        }
        return;
      }

      try {
        // C1 FIX: Read toilet token address from Supabase admin_config
        // Set this in Admin Panel → Config tab once partnership is confirmed
        const TOILET_TOKEN_TYPE = protocolConfig.toilet_token_address || null;

        if (!TOILET_TOKEN_TYPE) {
          // No address configured yet — toilet mode unavailable for real wallets
          const shouldEnable = shouldEnableToiletMode(0, isDemoToiletMode);
          setIsToiletHolder(shouldEnable);
        saveToiletHolderStatus(shouldEnable);
          setToiletBalance(0);
          if (shouldEnable) {
            applyToiletTheme();
            const streakData = getFlushStreak();
            setFlushStreak(streakData.count);
          } else {
            removeToiletTheme();
          }
          return;
        }
        
        const coinBalance = await suiClient.getBalance({
          owner: currentAccount.address,
          coinType: TOILET_TOKEN_TYPE,
        });

        const balance = Number(coinBalance.totalBalance) / 1_000_000_000;
        setToiletBalance(balance);
        
        
        const shouldEnable = shouldEnableToiletMode(balance, isDemoToiletMode);
        setIsToiletHolder(shouldEnable);
        saveToiletHolderStatus(shouldEnable);
        
        
        if (shouldEnable) {
          applyToiletTheme();
          const streakData = getFlushStreak();
          setFlushStreak(streakData.count);
        } else {
          removeToiletTheme();
        }
      } catch (error) {
        
        const shouldEnable = shouldEnableToiletMode(0, isDemoToiletMode);
        setIsToiletHolder(shouldEnable);
        saveToiletHolderStatus(shouldEnable);
        setToiletBalance(0);
        
        
        if (shouldEnable) {
          applyToiletTheme();
          const streakData = getFlushStreak();
          setFlushStreak(streakData.count);
        } else {
          removeToiletTheme();
        }
      }
    };

    checkToiletHoldings();
  }, [currentAccount?.address, isDemoMode, isDemoToiletMode, suiClient]);

  // Filter and set user objects
  useEffect(() => {
    if (isDemoMode) {
      const filtered = filterWhitelisted(DEMO_OBJECTS);
      setUserObjects(filtered);
      return;
    }

    // Clear all state when wallet disconnected - show zero until new wallet connects
    if (!currentAccount?.address) {
      setUserObjects([]);
      setProtectedAssets([]);
      setQuarantinedObjects([]);
      setPurityScore(0);
      setDistillationsCount(0);
      setCurrentTier(0);
      return;
    }

    // 🔥 USE allOwnedObjects (paginated) instead of ownedObjects (first page only)
    if (allOwnedObjects?.data) {
      const processObjects = async () => {
        // 🔥 COMPREHENSIVE LOGGING
        
        // Log all object types BEFORE filtering
        const objectTypes = {};
        allOwnedObjects.data.forEach(obj => {
          const type = obj.data?.type || 'unknown';
          const shortType = type.split('::').pop() || type;
          objectTypes[shortType] = (objectTypes[shortType] || 0) + 1;
        });
        
        Object.entries(objectTypes)
          .sort((a, b) => b[1] - a[1])
          .forEach(([type, count]) => {
          });
        
        
        // 💰 Fetch prices BEFORE filtering so dustDetection can use USD value gate.
        // Collect unique coin types from raw objects (string parsing only, no RPC).
        const rawCoinTypes = [...new Set(
          allOwnedObjects.data
            .map(obj => extractCoinType(obj?.data?.type || ''))
            .filter(Boolean)
        )];

        let preFetchPriceMap = {};
        let preFetchIconMap = {};
        if (rawCoinTypes.length > 0) {
          try {
            const { priceMap, iconMap } = await fetchTokenData(rawCoinTypes);
            preFetchPriceMap = priceMap;
            preFetchIconMap = iconMap;
            if (Object.keys(priceMap).length > 0) setTokenPrices(priceMap);
          } catch (err) {
            console.warn('Pre-filter price fetch failed (non-blocking):', err.message);
          }
        }

        // Stamp tokenPrice onto each raw object so dustDetection USD gate works
        const objectsWithPrices = allOwnedObjects.data.map(obj => {
          const coinType = extractCoinType(obj?.data?.type || '');
          const tokenPrice = coinType ? (preFetchPriceMap[coinType] || 0) : 0;
          return tokenPrice > 0 ? { ...obj, tokenPrice } : obj;
        });

        const filterResult = await filterObjectsForCleanup(
          objectsWithPrices,
          suiClient,
          DISTIL_PACKAGE_ID
        );
        
        const filteredObjects = filterResult.filtered;
        const filterStats = filterResult.stats;
        
        // Calculate which objects were filtered out (for Protected Assets modal)
        const shownObjectIds = new Set(
          filteredObjects.map(obj => obj.data?.objectId || obj.objectId)
        );
        
        const filteredOut = allOwnedObjects.data.filter(obj => {
          const objId = obj.data?.objectId || obj.objectId;
          return !shownObjectIds.has(objId);
        });

        // ✨ Enrich with metadata — string parsing only, no RPC calls
        const enriched = await enrichObjectsWithMetadata(suiClient, filteredObjects);
        
        // Mark whitelisted objects and apply any icons from pre-fetch
        // Simple objectId lookup — all whitelist entries are stored as individual objectIds
        const whitelist = getWhitelist();
        const enrichedWithWhitelist = enriched.map(obj => {
          const ct = obj?.enriched?.coinType;
          return {
            ...obj,
            isWhitelisted: !!whitelist[obj.data?.objectId],
            isNFT: obj.filterCategory === 'nft',
            isValuable: obj.filterCategory === 'valuable_object',
            filterInfo: {
              category: obj.filterCategory || 'unknown',
              reason: obj.filterReason || 'No filter info'
            },
            enriched: (ct && preFetchIconMap[ct])
              ? { ...obj.enriched, iconUrl: preFetchIconMap[ct] }
              : obj.enriched,
          };
        });

        // Whitelisted items stay OUT of userObjects — simple objectId check now works
        // for both singles and groups since all entries are stored as individual objectIds
        const cleanupObjects = enrichedWithWhitelist.filter(obj =>
          !obj.isWhitelisted &&
          !sessionQuarantinedIds.current.has(obj.data?.objectId)
        );

        setUserObjects(cleanupObjects);

        // 🔬 Background decimal verify — fires ONCE per session after first load.
        // Checks every coin type against RPC. If any decimals were wrong
        // (e.g. KNOWN_DECIMALS had bad value), re-processes once to show correct counts + prices.
        // Guard prevents infinite loop when corrections are always found (e.g. DEEP, NS).
        if (rawCoinTypes.length > 0 && !decimalsVerifiedRef.current) {
          decimalsVerifiedRef.current = true; // Set before async to prevent re-entry
          verifyDecimalsInBackground(suiClient, rawCoinTypes).then(corrections => {
            if (Object.keys(corrections).length > 0) {
              console.warn('🔧 Decimal corrections found — re-processing objects (once):', corrections);
              processObjects();
            }
          }).catch(() => {});
        }

        // 🔄 Background icon pass — cache is already warm so this is near-instant.
        // Only runs if there are icons the pre-fetch didn't cover (e.g. NFTs).
        const coinTypesForIcons = enrichedWithWhitelist
          .filter(obj => obj?.enriched?.isCoin && obj?.enriched?.coinType && !obj?.enriched?.iconUrl)
          .map(obj => obj.enriched.coinType);
        if (coinTypesForIcons.length > 0) {
          fetchTokenData(coinTypesForIcons)
            .then(({ priceMap, iconMap }) => {
              if (Object.keys(priceMap).length > 0) setTokenPrices(prev => ({ ...prev, ...priceMap }));
              if (Object.keys(iconMap).length > 0) {
                setUserObjects(prev => prev.map(obj => {
                  const ct = obj?.enriched?.coinType;
                  if (ct && iconMap[ct] && !obj?.enriched?.iconUrl) {
                    return { ...obj, enriched: { ...obj.enriched, iconUrl: iconMap[ct] } };
                  }
                  return obj;
                }));
              }
            })
            .catch(err => console.warn('Background icon fetch failed:', err.message));
        }
        
        // Store protected assets with enrichment and categorization
        const enrichedProtected = await enrichObjectsWithMetadata(suiClient, filteredOut);
        const protectedWithInfo = enrichedProtected.map(obj => {
          let filterCategory = 'valuable_object';
          let filterInfo = { category: 'Protected', reason: 'Valuable asset' };

          if (obj.data?.type?.includes('::sui::SUI')) {
            filterCategory = 'native_sui';
            filterInfo = { category: 'Native SUI', reason: 'Your SUI balance' };
          } else if (obj.data?.type?.includes(DISTIL_PACKAGE_ID)) {
            filterCategory = 'distil_object';
            filterInfo = { category: 'Distil', reason: 'Distil Protocol object' };
          } else if (obj.data?.type?.toLowerCase().includes('lp')) {
            filterInfo = { category: 'LP Token', reason: 'Liquidity pool position' };
          } else if (obj.data?.type?.toLowerCase().includes('stak')) {
            filterInfo = { category: 'Staking', reason: 'Staking position' };
          } else if (obj.usdValue && obj.usdValue > 0.5) {
            filterCategory = 'safe_value';
            filterInfo = { category: 'Safe Token', reason: 'Has value' };
          }

          // Stamp price + icon from pre-fetch (cache already warm, no extra network call)
          const ct = obj?.enriched?.coinType;
          const tokenPrice = ct ? (preFetchPriceMap[ct] || 0) : 0;
          const rawBal = obj?.enriched?.rawBalance;
          const decimals = obj?.enriched?.metadata?.decimals ?? 9;
          const tokenAmount = rawBal ? Number(rawBal) / Math.pow(10, decimals) : 0;
          const usdValue = tokenPrice > 0 && tokenAmount > 0
            ? tokenAmount * tokenPrice
            : (obj.usdValue || 0);
          const iconUrl = (ct && preFetchIconMap[ct])
            ? preFetchIconMap[ct]
            : (obj?.enriched?.iconUrl || (() => {
                // Non-coin objects (staking, LP etc): check if type string contains a known package address
                const objType = obj?.data?.type || '';
                for (const [pkg, url] of Object.entries(KNOWN_ICONS)) {
                  if (objType.includes(pkg)) return url;
                  // Also match short-form e.g. "0x2::sui::SUI" vs padded key "0x000...0002"
                  const shortPkg = pkg.replace(/^0x0+/, '0x');
                  if (objType.includes(shortPkg)) return url;
                }
                return null;
              })());

          return {
            ...obj,
            filterCategory,
            filterInfo,
            usdValue,
            tokenPrice,
            enriched: iconUrl ? { ...obj.enriched, iconUrl } : obj.enriched,
          };
        });
        // System-protected only — whitelisted items never go into protectedAssets
        setProtectedAssets(prev => [...protectedWithInfo]);
        
      };
      
      processObjects();
    } else {
      // No owned objects data, clear the list
      setUserObjects([]);
    }
  }, [allOwnedObjects, isDemoMode, currentAccount?.address, suiClient]);

  // Help guide is on-demand via the Help button — no auto-show

  // ✅ PRICE REFRESH: Update prices every 60 seconds via DexScreener
  // Uses ref to avoid restarting interval on every userObjects change
  const userObjectsRef = useRef(userObjects);
  useEffect(() => { userObjectsRef.current = userObjects; }, [userObjects]);

  useEffect(() => {
    if (isDemoMode || !currentAccount?.address) return;

    const refreshPrices = async () => {
      try {
        const coinTypes = userObjectsRef.current
          .filter(obj => obj?.enriched?.isCoin && obj?.enriched?.coinType)
          .map(obj => obj.enriched.coinType);
        if (coinTypes.length > 0) {
          const priceMap = await fetchPricesForTokens(coinTypes);
          if (Object.keys(priceMap).length > 0) setTokenPrices(priceMap);
        }
      } catch (error) {
        console.error('Price refresh failed:', error);
      }
    };

    const interval = setInterval(refreshPrices, 60000);
    return () => clearInterval(interval);
  }, [isDemoMode, currentAccount?.address]);

  // Toggle Demo Mode
  const toggleDemoMode = () => {
    const newDemoMode = !isDemoMode;
    setIsDemoMode(newDemoMode);
    
    if (newDemoMode) {
      // Inherit toilet mode context if user was already viewing toilet mode
      // (body has toilet-mode class = they clicked "Try Toilet Mode" before hitting demo)
      // Only reset if they were NOT in toilet mode context
      const wasInToiletContext = document.body.classList.contains('toilet-mode');
      if (!wasInToiletContext) {
        setIsDemoToiletMode(false);
        localStorage.setItem('distil_demo_toilet_mode', 'false');
      }
      // If they were in toilet mode context, isDemoToiletMode stays as-is

      const enrichedDemoObjects = DEMO_OBJECTS.map(obj => ({
        ...obj,
        isWhitelisted: isWhitelisted(obj.data.objectId),
        isNFT: false,
        filterInfo: {
          category: 'unknown',
          reason: 'Demo object'
        }
      }));
      
      setUserObjects(enrichedDemoObjects);
      setSelectedObjects([]);
      setDemoDistillations(0);
      setPurityScore(0);
      setCurrentTier(0);
      setDistillationsCount(0);
      setHasBadge(false);
      showToast('🎮 DEMO MODE - Practice away!', 'info');
    } else {
      // Always reset toilet mode on demo exit
      setIsDemoToiletMode(false);
      localStorage.setItem('distil_demo_toilet_mode', 'false');
      removeToiletTheme();

      setUserObjects([]);
      setSelectedObjects([]);
      setDemoDistillations(0);
      setPurityScore(0);
      setCurrentTier(0);
      setDistillationsCount(0);
      setHasBadge(false);
      showToast('Demo Mode Exited', 'info');
    }
  };

  // Exit Demo Mode
  const handleExitDemo = () => {
    setIsDemoMode(false);
    setIsDemoToiletMode(false);
    localStorage.setItem('distil_demo_toilet_mode', 'false');
    removeToiletTheme();
    setUserObjects([]);
    setProtectedAssets([]);
    setSelectedObjects([]);
    setDemoDistillations(0);
    setPurityScore(0);
    setCurrentTier(0);
    setDistillationsCount(0);
    setHasBadge(false);
    showToast('✨ Demo Mode Exited', 'success');
  };

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToastMessage({ message, type });
  };

  // Handle adding to whitelist
  const handleMarkAsSafe = (objectId) => {
    const success = addToWhitelist(objectId, 'Marked as safe via cleanup panel');
    if (success) {
      // Update the object's isWhitelisted flag instead of removing it
      setUserObjects(prev => prev.map(obj => 
        obj?.data?.objectId === objectId 
          ? { ...obj, isWhitelisted: true }
          : obj
      ));
      // Remove from selection
      setSelectedObjects(prev => prev.filter(id => id !== objectId));
      showToast('🛡️ PROTECTED - Safe from deletion', 'success');
      // Sync to Supabase for cross-device persistence (non-blocking)
      if (currentAccount?.address && !isDemoMode) {
        const updated = getWhitelist();
        saveWhitelistToSupabase(currentAccount.address, updated).catch(() => {});
      }
    }
  };

  // Show confirmation modal
  const handlePurifyClick = () => {
    if (selectedObjects.length === 0) {
      showToast('Select some trash to burn! 🔥', 'error');
      return;
    }

    // First-purge safety warning (real wallet only, not demo mode)
    if (!isDemoMode && currentAccount?.address) {
      const hasUserPurged = localStorage.getItem('has_user_purged') === 'true';
      const skipWarning = localStorage.getItem('skip_purge_warning') === 'true';
      if (!hasUserPurged && !skipWarning) {
        setShowFirstPurgeWarning(true);
        return;
      }
    }

    setShowConfirmation(true);
  };

  // Handle confirmation
  const handleConfirmPurify = () => {
    setShowConfirmation(false);
    handleDistillation();
  };

  // Handle cancel confirmation
  const handleCancelPurify = () => {
    setShowConfirmation(false);
  };

  // 🎮 FLUSH FRENZY HANDLER
  // Called only when wallet IS connected (FlushFrenzy component guards this)
  const handleGameComplete = (results) => {

    // 🚽 Track Flush Frenzy high score for Toilet badges
    trackToiletModeStats('flushFrenzyScore', results.score, currentAccount?.address);

    // ✅ FIX: Actually credit the reward to purity score immediately
    const reward = results.distilReward || 0;
    if (reward > 0) {
      const newScore = purityScore + reward;
      setPurityScore(newScore);

      // Persist to localStorage (wallet-scoped)
      if (currentAccount?.address) {
        localStorage.setItem(`distil-purity-score-${currentAccount.address}`, newScore.toString());
      }

      // Check for tier upgrade
      const newTier = getTierFromScore(newScore);
      if (newTier > currentTier) {
        setCurrentTier(newTier);
        const tierName = BADGE_TIERS && BADGE_TIERS[newTier] ? BADGE_TIERS[newTier].name : 'higher tier';
        showToast(`🎉 Tier Up! You're now a ${tierName}!`, 'success');
      } else {
        showToast(`🎉 Flush Frenzy! +${reward} $DISTIL earned! Score: ${results.score}`, 'success');
      }

      // Track for TGE + write to Supabase (drives leaderboard & TGE allocation)
      if (!isDemoMode && currentAccount?.address) {
        try {
          if (isTrackingEnabled()) {
            trackPurificationForTGE(0, reward, 0, currentAccount.address);
          }
          recordGameReward({
            walletAddress: currentAccount.address,
            purityEarned: reward,
            gameType: 'flush_frenzy',
            gameScore: results.score,
            isToiletMode: isToiletHolder,
          }).catch(e => console.error('Flush Frenzy Supabase error:', e));
        } catch (e) {
          console.error('Flush Frenzy tracking error:', e);
        }
      }
    } else {
      showToast(`🎮 Score: ${results.score} — nice run!`, 'success');
    }
  };

  // 🎟️ SCRATCH OFF HANDLER
  // Called only when wallet IS connected (ScratchOff component guards this)
  const handleScratchPrize = (prize) => {
    const reward = prize.amount || 0;
    if (reward > 0) {
      const newScore = purityScore + reward;
      setPurityScore(newScore);

      // Persist to localStorage (wallet-scoped)
      if (currentAccount?.address) {
        localStorage.setItem(`distil-purity-score-${currentAccount.address}`, newScore.toString());
      }

      // Check for tier upgrade
      const newTier = getTierFromScore(newScore);
      if (newTier > currentTier) {
        setCurrentTier(newTier);
        const tierName = BADGE_TIERS && BADGE_TIERS[newTier] ? BADGE_TIERS[newTier].name : 'higher tier';
        showToast(`🎉 Tier Up! You're now a ${tierName}!`, 'success');
      } else {
        showToast(`🎟️ Scratch Off! +${reward} $DISTIL earned!`, 'success');
      }

      // Track for TGE + write to Supabase
      if (!isDemoMode && currentAccount?.address) {
        try {
          if (isTrackingEnabled()) {
            trackPurificationForTGE(0, reward, 0, currentAccount.address);
          }
          recordGameReward({
            walletAddress: currentAccount.address,
            purityEarned: reward,
            gameType: 'scratch_off',
            gameScore: null,
            isToiletMode: isToiletHolder,
          }).catch(e => console.error('Scratch Off Supabase error:', e));
        } catch (e) {
          console.error('Scratch Off tracking error:', e);
        }
      }
    }
  };


  const handleDismissWarningBanner = () => {
    setWarningBannerDismissed(true);
    localStorage.setItem('distil_warning_banner_dismissed', 'true');
  };

  const handleDistillation = async () => {
    if (selectedObjects.length === 0) {
      showToast('Select some trash to burn! 🔥', 'error');
      return;
    }

    // 🔒 SECURITY: Batch size limit
    const MAX_OBJECTS_PER_TX = 50;
    if (selectedObjects.length > MAX_OBJECTS_PER_TX) {
      showToast(`Max ${MAX_OBJECTS_PER_TX} objects per purification — deselect some!`, 'error');
      return;
    }

    // 📅 DAILY LIMIT CHECK (real wallet only)
    if (!isDemoMode) {
      const isToilet = isToiletHolder || isDemoToiletMode;
      const remaining = getDailyRemaining(isToilet);
      if (remaining === 0) {
        showToast(`Daily limit reached! Come back in ${getResetCountdown()} 🕐`, 'error');
        return;
      }
      if (selectedObjects.length > remaining) {
        showToast(`You can only purify ${remaining} more object${remaining === 1 ? '' : 's'} today — deselect some!`, 'error');
        return;
      }
    }

    // NOTE: Do NOT setIsDistilling(true) here - wallet prompt fires first
    // Animation starts in onSuccess AFTER user approves in wallet

    // DEMO MODE — look-around only, no calculations, no saves
    if (isDemoMode) {
      setIsDistilling(true);

      setTimeout(() => {
        // Remove selected objects from UI — that's all we do in demo mode
        requestAnimationFrame(() => {
          setUserObjects((prev) => prev.filter(
            (obj) => obj?.data?.objectId && !selectedObjects.includes(obj.data.objectId)
          ));
          setSelectedObjects([]);
        });

        const randomMsg = DEGEN_SUCCESS_MESSAGES[Math.floor(Math.random() * DEGEN_SUCCESS_MESSAGES.length)];
        // Delay toast until after distillery animation completes (~5s)
        setTimeout(() => {
          showToast(`✨ ${randomMsg} (Demo — connect wallet to earn real $DISTIL!)`, 'info');
        }, 5200);

        // Keep animation alive for full particle cycle, then shut off
        setTimeout(() => {
          setIsDistilling(false);
        }, 5000);
      }, 2000);
      return;
    }


    // REAL MODE
    if (!currentAccount) {
      showToast('Please connect your wallet first', 'error');
      setIsDistilling(false);
      return;
    }

    // 🚨 POLISH PACKAGE: Error handling wrapper
    try {
      // ⏳ POLISH PACKAGE: Show loading
      setIsLoading(true);
      setLoadingMessage('Preparing transaction...');
      
      const tx = new Transaction();

      // Token deletion — transfer to burn address (permanent)
      selectedObjects.forEach((objectId) => {
        tx.transferObjects(
          [tx.object(objectId)],
          '0x0000000000000000000000000000000000000000000000000000000000000000'
        );
      });

      setLoadingMessage('Waiting for wallet approval...');
      // Don't show toast yet - wallet prompt is about to appear

      signAndExecuteTransaction(
        {
          transaction: tx,
        },
        {
          onSuccess: async (result) => {
            // Capture BEFORE any state changes - closure over selectedObjects can go stale
            const purifiedIds = [...selectedObjects];

            // 📅 Increment daily count immediately on success
            incrementDailyCount(purifiedIds.length);
            
            // START ANIMATION NOW - wallet approved, user can see it
            setIsDistilling(true);
            setIsLoading(false); // Clear loading spinner so animation is visible
            setLoadingMessage('');
            
            // Animation runs for 2500ms before we show toasts or clear state
            const ANIMATION_DURATION = 2500;
            await new Promise(r => setTimeout(r, ANIMATION_DURATION));
            
            try {

            const events = result.effects?.events || [];
            let totalPoints = 0;
            let totalSui = 0;

            events.forEach((event) => {
              if (event.type?.includes('PurificationEvent')) {
                const parsed = event.parsedJson || {};
                totalPoints += Number(parsed.purity_points_earned || 0);
                totalSui += Number(parsed.storage_rebate_sui || 0);
              }
            });

            // Fallback: if events didn't fire (common in beta), use 10pts per object
            if (totalPoints === 0) {
              totalPoints = selectedObjects.length * 10;
            }

            const categorizedObjects = selectedObjects.map(objId => {
              const obj = userObjects.find(o => o?.data?.objectId === objId);
              if (!obj || !obj.data) return null;
              const categoryInfo = categorizeObject(obj);
              const displayInfo = getObjectDisplayInfo(obj);
              return {
                id: objId,
                name: displayInfo.name || 'Unknown Object',
                category: categoryInfo.category.id,
                reason: categoryInfo.reason,
              };
            }).filter(Boolean);

            const gasUsed = Number(result.effects?.gasUsed?.computationCost || 0) / 1_000_000_000;
            
            let enhancedPoints = calculateEnhancedPurity(totalPoints, isToiletHolder);
            
            // Apply wheel prize if exists
            if (wheelPrize) {
              const rewards = applyWheelPrize({ purity: enhancedPoints }, wheelPrize);
              enhancedPoints = rewards.purity;

              // ✅ FIX: Handle non-$DISTIL wheel prizes that applyWheelPrize returns
              if (rewards.gasDiscount && rewards.gasDiscount > 0) {
                // Gas Saver prize — discount already passed, log it for now
                // Full gas discount implementation requires tx-level integration (V2)
              }
              if (rewards.streakBonus && rewards.streakBonus > 0) {
                // ✅ FIX: Streak Boost prize — add bonus flushes to streak
                const boostedStreak = flushStreak + rewards.streakBonus;
                setFlushStreak(boostedStreak);
                showToast(`🔥 Streak Boost! +${rewards.streakBonus} flush streak!`, 'success');
              }
              if (rewards.badgeUnlocked) {
                // ✅ FIX: Badge prize — record in localStorage for achievement system
                const earnedBadges = JSON.parse(localStorage.getItem('distil_earned_badges') || '[]');
                if (!earnedBadges.includes(rewards.badgeUnlocked)) {
                  earnedBadges.push(rewards.badgeUnlocked);
                  localStorage.setItem('distil_earned_badges', JSON.stringify(earnedBadges));
                }
                showToast(`🏆 Wheel Winner Badge unlocked!`, 'success');
              }

              showToast(`✨ Wheel bonus applied: ${wheelPrize.bonus}!`, 'special');
              clearWheelPrize();
              setWheelPrizeState(null);
            }
            
            // 💎 TASK 2: Apply holding bonus
            const userDistilBalance = getUserDistilBalance(null, false); // false = real wallet mode
            const { finalReward, bonusAmount, tier } = calculateBonusReward(enhancedPoints, userDistilBalance);
            enhancedPoints = finalReward; // Update with bonus applied
            
            // Show holding bonus toast if applicable
            // All toasts fire AFTER animation completes (5000ms) + stagger offset
            const TOAST_DELAY = 5200; // base: just after setIsDistilling(false)
            // Holding bonus toast — re-enable at TGE when $DISTIL balance query is live
            // if (bonusAmount > 0) {
            //   setTimeout(() => {
            //     showToast(`${tier.badge} ${tier.name} bonus: +${bonusAmount.toFixed(1)} $DISTIL!`, 'success');
            //   }, TOAST_DELAY);
            // }
            
            // 🔥 TASK 1: Track burns (Real Wallet Mode)
            const totalReward = enhancedPoints;
            const baseReward = totalReward / REWARD_SPLIT.USER_PERCENTAGE;
            const burnAmount = baseReward * REWARD_SPLIT.BURN_PERCENTAGE;
            trackBurn(burnAmount);
            
            // 📉 TASK 3: Track emissions (supply-based halving)
            trackEmission(baseReward); // Track full base reward
            
            // 🚀 BETA: Track for TGE if beta launched
            if (isTrackingEnabled() && !isDemoMode) {
              try {
                const trackingResult = trackPurificationForTGE(
                  selectedObjects.length,
                  enhancedPoints,
                  totalSui / 1_000_000_000,
                  currentAccount?.address
                );
                
                // Update daily streak
                const streakResult = await updateStreak(currentAccount?.address);
                if (streakResult.milestoneReached && streakResult.bonusAwarded > 0) {
                  // Credit streak milestone bonus to purityScore
                  enhancedPoints += streakResult.bonusAwarded;
                  setTimeout(() => {
                    showToast(
                      `🔥 ${streakResult.currentStreak}-Day Streak! +${streakResult.bonusAwarded} $DISTIL bonus!`,
                      'achievement'
                    );
                  }, TOAST_DELAY + 200);
                }
                
                // Check achievements
                const newAchievements = checkAchievements({
                  totalPurifications: distillationsCount + 1,
                  totalPurityEarned: purityScore + enhancedPoints,
                  currentStreak: streakResult.currentStreak,
                  longestStreak: streakResult.longestStreak,
                  totalObjectsCleaned: selectedObjects.length,
                  currentTier: currentTier,
                }, currentAccount?.address);
                
                // Show achievement notifications
                newAchievements.forEach((ach, i) => {
                  setTimeout(() => {
                    showToast(`🏆 Achievement: ${ach.name}!`, 'achievement');
                  }, TOAST_DELAY + 400 + (i * 600));
                });
                
              } catch (error) {
                console.error('Error tracking purification:', error);
              }
            }
            
            const newTier = getTierFromScore(purityScore + enhancedPoints);
            
            if (isToiletHolder || isDemoToiletMode) {
              triggerFlushEffect();
              const newStreak = updateFlushStreak();
              setFlushStreak(newStreak.count);
              
              
              if (newStreak.count > 1 && newStreak.count % 5 === 0) {
                showToast(`🔥 EPIC FLUSH STREAK: ${newStreak.count} flushes!`, 'success');
              }
            }

            // Fetch full transaction data to get accurate gasUsed including storageRebate
            // Retry with delay - tx needs a moment to be indexed before we can query it
            let storageRebateMist = 0;
            let actualSuiReclaimed = 0;
            const fetchGasData = async (digest, retries = 5, delayMs = 1500) => {
              for (let i = 0; i < retries; i++) {
                try {
                  await new Promise(r => setTimeout(r, i === 0 ? 1000 : delayMs));
                  const txData = await suiClient.getTransactionBlock({
                    digest,
                    options: { showEffects: true },
                  });
                  const gasUsedFull = txData?.effects?.gasUsed;
                  if (gasUsedFull) return gasUsedFull;
                } catch (e) {
                }
              }
              return null;
            };
            try {
              const gasUsedFull = await fetchGasData(result.digest);
              if (gasUsedFull) {
                const storageRebate = Number(gasUsedFull.storageRebate || 0);
                const computationCost = Number(gasUsedFull.computationCost || 0);
                storageRebateMist = storageRebate;
                const netMist = Math.max(0, storageRebate - computationCost);
                actualSuiReclaimed = netMist / 1_000_000_000;
              } else {
                console.warn('Could not fetch gas data after retries');
              }
            } catch (gasErr) {
              console.warn('Gas fetch error:', gasErr);
            }
            const actualPoints = selectedObjects.length * 10; // 10pts per object, beta

            // Only save real wallet purifications to persistent history (scoped to wallet address)
            if (!isDemoMode) {
              try {
                savePurification({
                  objectCount: purifiedIds.length,
                  purityEarned: enhancedPoints, // Use enhancedPoints (includes toilet + holding bonuses)
                  suiReclaimed: actualSuiReclaimed,
                  storageRebateMist: storageRebateMist,
                  gasUsed,
                  objectsPurified: categorizedObjects,
                  categoryBreakdown: getCategoryBreakdown(categorizedObjects.map(obj => ({
                    categoryInfo: { category: { id: obj.category } }
                  }))),
                  tierUpOccurred: newTier > currentTier,
                  newTierName: newTier > currentTier && BADGE_TIERS && BADGE_TIERS[newTier] ? BADGE_TIERS[newTier].name : null,
                  transactionHash: result.digest,
                  blockNumber: result.effects?.checkpoint,
                  isDemoMode: false,
                }, currentAccount?.address);
                console.log('✅ History saved for wallet:', currentAccount?.address);
              } catch (historyErr) {
                console.error('❌ History save failed (non-blocking):', historyErr);
              }
            }

            // Record to Supabase for leaderboard + protocol stats
            if (!isDemoMode && currentAccount?.address) {
              const categoryData = getCategoryBreakdown(categorizedObjects.map(obj => ({
                categoryInfo: { category: { id: obj.category } }
              })));
              recordToSupabase({
                walletAddress: currentAccount.address,
                objectCount: purifiedIds.length,
                purityEarned: enhancedPoints, // Use enhancedPoints (includes toilet + holding bonuses)
                suiReclaimed: actualSuiReclaimed,
                gasUsed,
                storageRebateMist: storageRebateMist,
                transactionHash: result.digest,
                categoryBreakdown: categoryData,
                isToiletMode: isToiletHolder,
              }).catch(err => console.error('Supabase record failed (non-blocking):', err));
            }
            
            // Remove deleted objects from UI immediately using captured IDs
            requestAnimationFrame(() => {
              setUserObjects((prev) => prev.filter((obj) =>
                obj?.data?.objectId && !purifiedIds.includes(obj.data.objectId)
              ));
            });

            const newPurityScore = purityScore + enhancedPoints;
            setPurityScore(newPurityScore);
            // Persist to localStorage so it survives page refresh
            if (currentAccount?.address && !isDemoMode) {
              localStorage.setItem(`distil-purity-score-${currentAccount.address}`, newPurityScore.toString());
            }
            // Referral: check if this purification qualifies the referred user
            if (currentAccount?.address && !isDemoMode) {
              checkReferralCompletion(currentAccount.address, newPurityScore).then(result => {
                if (result.completed) {
                  showToast('🎉 Referral qualified! 50 $DISTIL welcome bonus earned!', 'success');
                }
              }).catch(() => {});
            }
            const finalTier = getTierFromScore(purityScore + enhancedPoints);

            if (finalTier > currentTier) {
              const badge = document.querySelector('.badge-image');
              if (badge) {
                badge.style.animation = 'tier-up-animation 1s ease-out';
                setTimeout(() => {
                  badge.style.animation = '';
                }, 1000);
              }
              setCurrentTier(finalTier);
              const tierName = BADGE_TIERS && BADGE_TIERS[finalTier] ? BADGE_TIERS[finalTier].name : 'higher tier';
              // Delay toast until after distillery animation completes
              setTimeout(() => {
                showToast(`🎉 Tier Up! You're now a ${tierName}!`, 'success');
              }, TOAST_DELAY);
              
              // 🐦 Share tier upgrade via toast (non-blocking)
              setTimeout(() => {
                showToast(`🐦 Share your ${tierName} achievement on 𝕏?`, 'info');
              }, TOAST_DELAY + 1500);
            } else {
              // Random degen success message — fires after animation completes
              const randomMsg = DEGEN_SUCCESS_MESSAGES[Math.floor(Math.random() * DEGEN_SUCCESS_MESSAGES.length)];
              setTimeout(() => {
                showToast(randomMsg, 'success');
              }, TOAST_DELAY);
            }

            const achievementStats = {
              totalPurifications: distillationsCount + 1,
              totalPurityEarned: purityScore + enhancedPoints,
              currentTier: finalTier,
              totalObjectsCleaned: (distillationsCount + 1) * selectedObjects.length,
              isToiletMode: isToiletHolder,
              toiletFlushes: isToiletHolder ? flushStreak : 0,
            };
            const newAchievements = checkAchievements(achievementStats, currentAccount?.address);
            newAchievements.forEach((ach, i) => {
              setTimeout(() => {
                showToast(`🏆 Achievement Unlocked: ${ach.name}!`, 'success');
              }, TOAST_DELAY + 400 + (i * 600));
            });
            
            // 🏆 POLISH PACKAGE: Check milestone notifications
            setTimeout(() => {
              const newPurifications = distillationsCount + 1;
              
              // Check purification milestone
              const purificationMilestone = checkPurificationMilestone(newPurifications);
              if (purificationMilestone && newPurifications !== lastPurificationCount) {
                const toast = formatMilestoneToast(purificationMilestone);
                showToast(toast.message, 'achievement');
                setTimeout(() => showToast(toast.detail, 'info'), 600);
                setLastPurificationCount(newPurifications);
              }
            }, TOAST_DELAY + 800);

            setSelectedObjects([]);
            
            // Keep animation alive for full particle cycle, then shut off
            // Clear loading immediately but keep distilling alive for the visual
            setIsLoading(false);
            setTimeout(() => {
              setIsDistilling(false);
            }, 5000);
            
            // NOTE: Do NOT refetch objects after purification
            // refetchObjects() rewrites the list from chain before deletion propagates
            // Local state filter handles UI update immediately
            try { refetchBadge(); } catch(e) {}
            clearPriceCache(); // Force fresh prices on next load
            } catch(onSuccessError) {
              // Safety net - never leave loading spinner stuck
              console.error('onSuccess error:', onSuccessError);
              setIsLoading(false);
              setIsDistilling(false);
              showToast('Purification complete! (UI update error)', 'success');
            }
          },
          onError: (error) => {
            console.error('Distillation failed:', error);
            setIsLoading(false);
            setIsDistilling(false);
            if (isMaliciousError(error)) {
              quarantineObjects([...selectedObjects]);
              showToast('⚠️ Malicious object detected — moved to Quarantine', 'error');
              setShowQuarantineModal(true);
            } else {
              setErrorDetails(error);
              setShowError(true);
              showToast(`Purification failed: ${error.message}`, 'error');
            }
          },
        }
      );
    } catch (error) {
      console.error('Distillation error:', error);
      setIsLoading(false);
      setIsDistilling(false);
      if (isMaliciousError(error)) {
        quarantineObjects([...selectedObjects]);
        showToast('⚠️ Malicious object detected — moved to Quarantine', 'error');
        setShowQuarantineModal(true);
      } else {
        setErrorDetails(error);
        setShowError(true);
        showToast(`Error: ${error.message}`, 'error');
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // 🚨 MALICIOUS OBJECT HANDLING
  // ═══════════════════════════════════════════════════════════════════════

  const isMaliciousError = (error) => {
    if (!error) return false;
    const msg = (error.message || error.toString() || '').toLowerCase();
    console.warn('[Distil] isMaliciousError checking:', msg);
    return (
      msg.includes('malicious') ||
      msg.includes('halted') ||
      msg.includes('dangerous') ||
      msg.includes('fraud') ||
      msg.includes('security') ||
      msg.includes('threat') ||
      msg.includes('unsafe') ||
      msg.includes('suspicious') ||
      msg.includes('blocked') ||
      // Slush "Return to safety" = user rejected because wallet blocked it
      msg.includes('user rejected the request') ||
      msg.includes('user rejected') ||
      msg.includes('transaction cancelled') ||
      msg.includes('rejected by user') ||
      msg.includes('trpcclienterror')
    );
  };

  const quarantineObjects = (objectIds) => {
    // Register in session ref immediately — processObjects will exclude these
    // on every subsequent allOwnedObjects re-fetch for the rest of this page session
    objectIds.forEach(id => sessionQuarantinedIds.current.add(id));
    setUserObjects(prev => {
      const toQuarantine = prev.filter(obj => objectIds.includes(obj?.data?.objectId));
      const remaining = prev.filter(obj => !objectIds.includes(obj?.data?.objectId));
      const flagged = toQuarantine.map(obj => ({
        ...obj,
        isQuarantined: true,
        quarantinedAt: Date.now(),
        filterCategory: 'quarantined',
      }));
      setQuarantinedObjects(q => {
        const existingIds = new Set(q.map(o => o?.data?.objectId));
        const newItems = flagged.filter(o => !existingIds.has(o?.data?.objectId));
        const updated = [...q, ...newItems];
        // Persist to Supabase whenever quarantine list changes
        if (currentAccount?.address) {
          saveQuarantineToSupabase(currentAccount.address, updated).catch(() => {});
        }
        return updated;
      });
      return remaining;
    });
    setSelectedObjects(prev => prev.filter(id => !objectIds.includes(id)));
  };

  // Return a quarantined object to the cleanup list (false positive recovery)
  const handleReturnFromQuarantine = (objectId) => {
    // Remove from session ref so processObjects will include it in cleanup list again
    sessionQuarantinedIds.current.delete(objectId);
    setQuarantinedObjects(prev => {
      const returning = prev.find(o => o?.data?.objectId === objectId);
      const updated = prev.filter(o => o?.data?.objectId !== objectId);
      // Persist updated quarantine list to Supabase
      if (currentAccount?.address) {
        saveQuarantineToSupabase(currentAccount.address, updated).catch(() => {});
      }
      // Return the object to userObjects (strip quarantine flags)
      if (returning) {
        const restored = { ...returning };
        delete restored.isQuarantined;
        delete restored.quarantinedAt;
        delete restored.filterCategory;
        setUserObjects(prev => [...prev, restored]);
      }
      return updated;
    });
  };

  // Unwhitelist an object - moves it back from cleanup list
  // Unwhitelist an object - moves it back to cleanup list
  // objectId is always an actual objectId now (flat storage, no coinType keys)
  // isGroupType is kept as param for future use but logic is identical
  const handleUnwhitelist = async (objectId, isGroupType = false) => {
    // Read the cached object BEFORE deleting from ref
    const cachedObj = recentlyWhitelistedRef.current[objectId];

    // Now clear from cache and register in session ref
    delete recentlyWhitelistedRef.current[objectId];
    sessionRemovedFromWhitelist.current.add(objectId);

    // Read localStorage metadata BEFORE removing (removeFromWhitelist deletes it)
    const whitelist = getWhitelist();
    const storedMeta = whitelist[objectId]; // has name, symbol, iconUrl, coinType etc.

    // Remove from localStorage
    removeFromWhitelist(objectId);

    // Save to Supabase
    if (currentAccount?.address && !isDemoMode) {
      const updated = getWhitelist();
      try {
        await saveWhitelistToSupabase(currentAccount.address, updated);
      } catch (e) {
        console.error('Failed to save whitelist removal to Supabase:', e);
      }
    }

    // Immediately restore object to userObjects so it appears in cleanup list
    // without requiring a page refresh.
    // Priority: recentlyWhitelistedRef (full object) → localStorage metadata (lightweight stub)
    if (cachedObj) {
      // Full object available — restore directly
      const restored = { ...cachedObj, isWhitelisted: false, filterCategory: undefined, filterInfo: undefined };
      setUserObjects(prev => [...prev, restored]);
    } else if (storedMeta?.coinType || storedMeta?.name) {
      // Post-refresh: reconstruct a minimal display object from stored metadata
      // processObjects will replace this with the full chain object on next run,
      // but this makes it visible immediately in the cleanup list
      const stub = {
        data: { objectId },
        isWhitelisted: false,
        filterCategory: undefined,
        filterInfo: undefined,
        enriched: {
          isCoin: !!storedMeta.coinType,
          coinType: storedMeta.coinType || null,
          symbol: storedMeta.symbol || null,
          displayName: storedMeta.name || null,
          iconUrl: storedMeta.iconUrl || null,
          rawBalance: storedMeta.rawBalance || 0,
          formattedBalance: storedMeta.rawBalance > 0
            ? (storedMeta.rawBalance / Math.pow(10, storedMeta.decimals ?? 9))
                .toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })
            : '0',
          metadata: { decimals: storedMeta.decimals ?? 9 },
        },
      };
      setUserObjects(prev => [...prev, stub]);
    }
    // If neither is available, processObjects will restore on next chain fetch (page refresh)

    showToast(`↩️ Removed from whitelist`, 'info');
  };

  // Toggle object selection
  // 🔥 GROUP TOKENS BY COIN TYPE
  const groupTokens = (objects) => {
    const groups = {};
    const ungrouped = [];
    
    objects.forEach(obj => {
      // Only group coins (enriched tokens)
      if (obj?.enriched?.isCoin && obj?.enriched?.coinType) {
        const coinType = obj.enriched.coinType;
        
        if (!groups[coinType]) {
          groups[coinType] = {
            coinType,
            name: obj.enriched.displayName, // ✅ ADD: name property
            symbol: obj.enriched.symbol,
            displayName: obj.enriched.displayName,
            iconUrl: obj.enriched.iconUrl,
            objects: [],
            totalBalance: 0,
            decimals: obj.enriched.metadata?.decimals || 9,
          };
        }
        
        groups[coinType].objects.push(obj);
        const balance = parseFloat(obj.enriched.rawBalance || 0);
        groups[coinType].totalBalance += balance;
      } else {
        // Non-coins stay ungrouped (NFTs, etc.)
        ungrouped.push(obj);
      }
    });
    
    // ✅ Sort: highest USD value first, then price unavailable by object count desc
    const groupedArray = [];
    Object.values(groups).forEach(group => {
      if (group.objects.length === 1) {
        ungrouped.unshift(group.objects[0]);
      } else {
        groupedArray.push({
          ...group,
          isGroup: true,
          objectCount: group.objects.length,
        });
      }
    });

    groupedArray.sort((a, b) => {
      // ✅ FIX: Sort by USD value (highest first), then by balance, then alphabetically
      const aPrice = tokenPrices[a.coinType] || 0;
      const bPrice = tokenPrices[b.coinType] || 0;
      
      const aUsdValue = (a.totalBalance / Math.pow(10, a.decimals)) * aPrice;
      const bUsdValue = (b.totalBalance / Math.pow(10, b.decimals)) * bPrice;
      
      // First: Sort by USD value (highest first)
      if (aUsdValue > 0 || bUsdValue > 0) {
        if (aUsdValue !== bUsdValue) {
          return bUsdValue - aUsdValue; // Descending by USD value
        }
      }
      
      // Second: If no USD value, sort by whether they have balance
      const aHasBalance = a.totalBalance > 0;
      const bHasBalance = b.totalBalance > 0;
      if (aHasBalance && !bHasBalance) return -1;
      if (!aHasBalance && bHasBalance) return 1;
      
      // Third: Sort alphabetically
      return a.symbol.localeCompare(b.symbol);
    });
    
    // ✅ FIX: Sort ungrouped - USD value first (highest), then balance, then alphabetically
    ungrouped.sort((a, b) => {
      const aBalance = parseFloat(a.enriched?.rawBalance || a.balance || 0);
      const bBalance = parseFloat(b.enriched?.rawBalance || b.balance || 0);
      
      // Get prices and calculate USD values
      const aCoinType = a.enriched?.coinType || a.data?.type;
      const bCoinType = b.enriched?.coinType || b.data?.type;
      const aPrice = tokenPrices[aCoinType] || 0;
      const bPrice = tokenPrices[bCoinType] || 0;
      const aDecimals = a.enriched?.metadata?.decimals || 9;
      const bDecimals = b.enriched?.metadata?.decimals || 9;
      
      const aUsdValue = (aBalance / Math.pow(10, aDecimals)) * aPrice;
      const bUsdValue = (bBalance / Math.pow(10, bDecimals)) * bPrice;
      
      // First: Sort by USD value (highest first)
      if (aUsdValue > 0 || bUsdValue > 0) {
        if (aUsdValue !== bUsdValue) {
          return bUsdValue - aUsdValue; // Descending by USD value
        }
      }
      
      // Second: If no USD value, sort by whether they have balance
      const aHasBalance = aBalance > 0;
      const bHasBalance = bBalance > 0;
      if (aHasBalance && !bHasBalance) return -1;
      if (!aHasBalance && bHasBalance) return 1;
      
      // Third: Sort alphabetically by name
      const aName = getObjectDisplayInfo(a).name;
      const bName = getObjectDisplayInfo(b).name;
      return aName.localeCompare(bName);
    });
    
    return { groups: groupedArray, ungrouped };
  };

  // Toggle group expansion
  const toggleGroupExpansion = (coinType) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(coinType)) {
        newSet.delete(coinType);
      } else {
        newSet.add(coinType);
      }
      return newSet;
    });
  };

  // Select/deselect entire group
  const toggleGroupSelection = (group) => {
    const groupObjectIds = group.objects.map(obj => obj.data.objectId);
    const allSelected = groupObjectIds.every(id => selectedObjects.includes(id));
    
    if (allSelected) {
      // Deselect all in group
      setSelectedObjects(prev => prev.filter(id => !groupObjectIds.includes(id)));
    } else {
      // Select all in group
      setSelectedObjects(prev => [...new Set([...prev, ...groupObjectIds])]);
    }
  };

  const toggleObjectSelection = (objectId) => {
    // Check if object is whitelisted - if so, don't allow selection!
    const obj = userObjects.find(o => o?.data?.objectId === objectId);
    if (obj?.isWhitelisted) {
      showToast('Protected object cannot be selected', 'warning');
      return;
    }
    
    setSelectedObjects((prev) =>
      prev.includes(objectId)
        ? prev.filter((id) => id !== objectId)
        : [...prev, objectId]
    );
  };

  // Select all objects (excluding whitelisted!)
  const selectAllObjects = () => {
    const selectableObjects = userObjects
      .filter(obj => {
        if (!obj?.data?.objectId || obj?.isWhitelisted) return false;
        // Tokens only — NFTs hidden from cleanup
        return obj?.enriched?.isCoin === true;
      });
    
    setSelectedObjects(selectableObjects.map(obj => obj.data.objectId));
    
    const whitelistedCount = userObjects.filter(obj => obj?.isWhitelisted).length;
    if (whitelistedCount > 0) {
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedObjects([]);
  };

  // Show landing page
  if (showLanding) {
    return <LandingPage onEnterApp={() => {
      sessionStorage.setItem('distil_entered_app', 'true');
      setShowLanding(false);
    }} />;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎯 ELLIPSIS MENU COMPONENT
  // ═══════════════════════════════════════════════════════════════════════════
  
  const ObjectMenu = ({ objectId, objectType, displayName, menuType = 'singleObject' }) => {
    const isOpen = openMenuId === objectId;
    const buttonRef = React.useRef(null);
    const [dropdownStyle, setDropdownStyle] = React.useState({});

    // Calculate fixed position based on button location + objects-list boundary
    React.useEffect(() => {
      if (isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const scrollContainer = buttonRef.current.closest('.objects-list');
        const dropdownHeight = 220;

        let opensUpward = false;
        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const spaceBelow = containerRect.bottom - rect.bottom;
          opensUpward = spaceBelow < dropdownHeight;
        } else {
          opensUpward = (window.innerHeight - rect.bottom) < dropdownHeight;
        }

        setDropdownStyle({
          position: 'fixed',
          right: window.innerWidth - rect.right,
          ...(opensUpward
            ? { bottom: window.innerHeight - rect.top + 4 }
            : { top: rect.bottom + 4 }
          ),
          zIndex: 99999,
          minWidth: '220px',
        });
      }
    }, [isOpen]);

    // menuType: 'groupHeader' | 'groupedObject' | 'singleObject'
    const showExplorerLinks = menuType !== 'groupHeader';
    const showWhitelist = menuType !== 'groupedObject';

    const dropdownContent = (
      <div
        className={`object-menu-dropdown portal-dropdown${isToiletHolder ? ' toilet-mode' : ''}`}
        style={dropdownStyle}
      >
        {/* 🎯 Token name header */}
        <div className="menu-header">
          {displayName}
        </div>
        <div className="menu-divider" />
              
              {/* Explorer Links - Only for groupedObject and singleObject */}
              {showExplorerLinks && (
                <>
                  <button
                    className="menu-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`https://suiscan.xyz/mainnet/object/${objectId}`, '_blank');
                      setOpenMenuId(null);
                    }}
                  >
                    <span className="menu-item-icon">🔍</span>
                    <span className="menu-item-text">View on SuiScan</span>
                  </button>
                  
                  <button
                    className="menu-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`https://suivision.xyz/object/${objectId}`, '_blank');
                      setOpenMenuId(null);
                    }}
                  >
                    <span className="menu-item-icon">📊</span>
                    <span className="menu-item-text">View on SuiVision</span>
                  </button>
                  
                  <div className="menu-divider" />
                </>
              )}
              
              {/* Whitelist - Only for groupHeader and singleObject */}
              {showWhitelist && (
                <button
                  className="menu-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Always check/store by objectId — flat storage, no coinType keys
                    // For groups, objectId is the first object's id (used only for isWhitelisted check)
                    const checkId = objectId; // objectId is always an actual objectId
                    const alreadyWhitelisted = isWhitelisted(checkId);
                    
                    if (!alreadyWhitelisted) {
                      const reason = menuType === 'groupHeader' 
                        ? `Whitelisted ${displayName} token group`
                        : `Whitelisted ${displayName}`;

                      // Collect objects to whitelist from userObjects synchronously
                      // For groups: all objects matching coinType. For singles: just this objectId.
                      const currentObjects = [...(window._distilUserObjectsSnapshot || [])];
                      // Use a direct read approach: filter userObjects snapshot
                      // We'll populate via setUserObjects updater but also capture synchronously
                      let objectsToWhitelist = [];

                      setUserObjects(prev => {
                        objectsToWhitelist = []; // reset for strict mode double-invoke safety
                        const remaining = [];
                        prev.forEach(obj => {
                          const objId = obj.data?.objectId;
                          const objType = obj.enriched?.coinType || obj.data?.type;
                          const shouldWhitelist = menuType === 'groupHeader'
                            ? objType === objectType
                            : objId === objectId;
                          if (shouldWhitelist) {
                            objectsToWhitelist.push(obj);
                            // Cache immediately for WhitelistPanel display before refresh
                            if (objId) recentlyWhitelistedRef.current[objId] = obj;
                          } else {
                            remaining.push(obj);
                          }
                        });
                        // Add to whitelist inside updater with full display metadata
                        // Metadata stored in localStorage so WhitelistPanel works after refresh
                        // without needing recentlyWhitelistedRef or live userObjects
                        objectsToWhitelist.forEach(obj => {
                          const objId = obj.data?.objectId;
                          if (objId) {
                            const enriched = obj.enriched || {};
                            addToWhitelistWithMeta(objId, reason, {
                              name: enriched.displayName || enriched.symbol || null,
                              symbol: enriched.symbol || null,
                              iconUrl: enriched.iconUrl || null,
                              coinType: enriched.coinType || null,
                              rawBalance: enriched.rawBalance || 0,
                              decimals: enriched.metadata?.decimals ?? 9,
                            });
                          }
                        });
                        return remaining;
                      });

                      // Save to Supabase after setState (non-blocking)
                      if (currentAccount?.address && !isDemoMode) {
                        setTimeout(() => {
                          const updated = getWhitelist();
                          if (Object.keys(updated).length > 0) {
                            saveWhitelistToSupabase(currentAccount.address, updated).catch(() => {});
                          }
                        }, 50);
                      }

                      showToast(`🛡️ ${displayName} added to Whitelist`, 'success');
                    } else {
                      showToast(`${displayName} is already protected`, 'info');
                    }
                    setOpenMenuId(null);
                  }}
                >
                  <span className="menu-item-icon">
                    {isWhitelisted(objectId) ? '✅' : '🛡️'}
                  </span>
                  <span className="menu-item-text">
                    {isWhitelisted(objectId) ? 'Whitelisted' : 'Whitelist'}
                  </span>
                </button>
              )}
            </div>
    );

    return (
      <>
        <button
          ref={buttonRef}
          className="object-menu-button"
          onClick={(e) => {
            e.stopPropagation();
            setOpenMenuId(isOpen ? null : objectId);
          }}
          title="Options"
        >
          ⋮
        </button>

        {isOpen && ReactDOM.createPortal(
          <>
            {/* Backdrop — closes menu on outside click */}
            <div
              onClick={(e) => {
                e.stopPropagation();
                setOpenMenuId(null);
              }}
              style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                zIndex: 99998,
              }}
            />
            {dropdownContent}
          </>,
          document.body
        )}
      </>
    );
  };

  // Main app view
  return (
    <div className="app">
      {/* 🔴 TEMP DEBUG OVERLAY - REMOVE AFTER MOBILE FIX */}
      
      {/* 🎉 TASK 3: Milestone celebrations */}
      <MilestoneAlert />
      
      {/* 🚨 POLISH PACKAGE: Error Modal */}
      <ErrorModal
        isOpen={showError}
        onClose={() => setShowError(false)}
        error={errorDetails}
        onRetry={() => {
          setShowError(false);
          if (pendingPurification) {
            handleConfirmPurification();
          }
        }}
        title="Transaction Failed"
        showRetry={!!pendingPurification}
      />
      
      {/* ⏳ POLISH PACKAGE: Loading Overlay */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-overlay-content">
            <LoadingSpinner 
              message={loadingMessage}
              size="large"
              variant="purple"
            />
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className="app-header">
        <img 
          src="/images/distil-logo-animated.svg" 
          alt="DISTIL" 
          className="logo-animated"
          style={{ height: '65px', width: 'auto' }}
        />

        <motion.button
          className="home-button"
          onClick={() => {
            sessionStorage.removeItem('distil_entered_app');
            setShowLanding(true);
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          ← Home
        </motion.button>

        <div className="header-controls">
          {isDemoMode && (
            <>
              <button className="demo-mode-badge">
                🎮 DEMO MODE
              </button>
              <button
                className="exit-demo-button"
                onClick={handleExitDemo}
              >
                ✨ EXIT DEMO
              </button>
            </>
          )}

          {!currentAccount && !isDemoMode && (
            <motion.button
              className="demo-cta-button"
              onClick={toggleDemoMode}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              🎮 TRY DEMO
            </motion.button>
          )}

          {!isDemoMode && (
            <motion.button
              className={`toilet-mode-toggle ${isDemoToiletMode ? 'active' : ''}`}
              onClick={() => {
                const newMode = !isDemoToiletMode;
                setIsDemoToiletMode(newMode);
                if (newMode && !localStorage.getItem('distil_warrior_seen')) {
                  setShowWarriorActivation(true);
                  localStorage.setItem('distil_warrior_seen', 'true');
                }
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Toggle Toilet Mode"
            >
              🚽 {isDemoToiletMode ? 'Exit' : 'Try'} Toilet Mode
            </motion.button>
          )}

          <motion.button
            className="history-button"
            onClick={() => setShowHistory(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="View purification history"
          >
            📜 History
          </motion.button>

          <motion.button
            className="stats-button"
            onClick={() => setShowStats(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="View protocol statistics"
          >
            📊 Stats
          </motion.button>

          {(currentAccount?.address && !isDemoMode) && (
            <motion.button
              className="referral-invite-btn"
              onClick={() => setShowReferral(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Invite friends & earn $DISTIL"
            >
              🤝 Invite
            </motion.button>
          )}

          <motion.button
            className="whitelist-button"
            onClick={() => setShowWhitelist(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Manage whitelist"
          >
            🛡️ Whitelist
          </motion.button>

          <motion.button
            className="help-button"
            onClick={() => setShowWhitepaper(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Documentation & Whitepaper"
          >
            📚 Docs
          </motion.button>



          {showInstallBtn && (
            <button
              className="pwa-install-btn"
              onClick={handleInstallClick}
              title="Install Distil App"
            >
              📲 Install App
            </button>
          )}
          <div className="connect-wallet-wrap">
            <ConnectButton className="wallet-button-compact" />
          </div>
        </div>
      </header>

      {/* 🎉 Referral Welcome Banner — shown when referred user connects */}
      {referralWelcome && (
        <ReferralWelcomeBanner
          referrerAddress={referralWelcome.referrerAddress}
          walletAddress={currentAccount?.address}
          onDismiss={() => setReferralWelcome(null)}
          onStartCleaning={() => {
            setReferralWelcome(null);
            document.querySelector('.cleanup-panel')?.scrollIntoView({ behavior: 'smooth' });
          }}
        />
      )}

      {/* 🎯 NEW DASHBOARD GRID LAYOUT */}
      <DashboardGrid
        purityScore={purityScore}
        currentTier={currentTier}
        isWalletConnected={!!currentAccount?.address}
        isDemoMode={isDemoMode}
        isToiletHolder={isToiletHolder}
        isDemoToiletMode={isDemoToiletMode}
        isWheelAvailable={isWheelAvailable}
        setShowSpinWheel={setShowSpinWheel}
        setShowFlushFrenzy={setShowFlushFrenzy}
        setShowScratchOff={setShowScratchOff}
        quarantineButton={
          quarantinedObjects.length > 0 ? (
            <button
              onClick={() => setShowQuarantineModal(true)}
              className="quarantine-compact-btn"
            >
              <span className="quarantine-btn-icon">⚠️</span>
              <span className="quarantine-btn-text">
                <span className="quarantine-btn-label">Quarantine</span>
                <span className="quarantine-btn-count">{quarantinedObjects.length} flagged</span>
              </span>
            </button>
          ) : null
        }
        protectedAssetsButton={
                          protectedAssets.length > 0 && (currentAccount?.address || isDemoMode) ? (
            <button
              onClick={() => setShowProtectedModal(true)}
              className="protected-assets-compact-btn"
            >
              <span className="protected-icon">{isToiletHolder ? '🚽' : '🛡️'}</span>
              <span className="protected-text">
                <span className="protected-label">Protected</span>
                <span className="protected-count">{protectedAssets.filter(o => o.filterCategory !== 'whitelisted').length} assets</span>
              </span>
            </button>
          ) : null
        }
        cleanupContent={
          <>
            <div className="panel-header">
            <h2>
              Wallet Cleanup {isDemoMode && <span className="demo-badge-inline">DEMO</span>}
              {isToiletHolder && (
                <>
                  <span className="toilet-mode-indicator">
                    <span className="toilet-mode-indicator-icon">🚽</span>
                    <span className="toilet-mode-indicator-text">
                      <span className="toilet-mode-indicator-title">TOILET MODE</span>
                      <span className="toilet-mode-indicator-subtitle">+15% | +2 Gas Rebate</span>
                    </span>
                  </span>
                  {flushStreak > 1 && (
                    <span className="toilet-mode-indicator flush-streak-inline">
                      <span className="toilet-mode-indicator-icon">🔥</span>
                      <span className="toilet-mode-indicator-text">
                        <span className="toilet-mode-indicator-title">{flushStreak}</span>
                        <span className="toilet-mode-indicator-subtitle">FLUSH STREAK</span>
                      </span>
                    </span>
                  )}
                </>
              )}
            </h2>
            <p className="panel-subtitle">Test purifying scams, spam, and dust</p>
          </div>

          {/* 📅 DAILY LIMIT INDICATOR */}
          {!isDemoMode && currentAccount && (
            <DailyLimitIndicator isToiletMode={isToiletHolder || isDemoToiletMode} />
          )}

          {/* 🪙 TOKEN-ONLY NOTICE */}
          {(() => {
            const nftCount = userObjects.filter(o => o?.enriched?.isCoin !== true && !o?.isWhitelisted).length;
            if (nftCount === 0) return null;
            return (
              <div className={`nft-coming-notice ${isToiletHolder ? 'toilet-mode' : ''}`}>
                <span className="notice-icon">🪙</span>
                <div className="notice-text">
                  <span className="notice-title">Tokens only — {nftCount} NFT{nftCount > 1 ? 's' : ''} hidden</span>
                  <span className="notice-sub">NFT vault coming in V2 — done right, not rushed</span>
                </div>
              </div>
            );
          })()}

          <div className="selection-controls">
            {(() => {
              const tokensOnly = userObjects.filter(o => o?.enriched?.isCoin === true);
              const { groups, ungrouped } = groupTokens(tokensOnly);
              const totalGroups = groups.length + ungrouped.length;
              const totalObjects = tokensOnly.length;
              
              return (
                <>
                  <button
                    className="select-all-button"
                    onClick={selectAllObjects}
                    disabled={userObjects.length === 0}
                  >
                    <span className="select-all-text">
                      Select All ({totalGroups} {totalGroups === 1 ? 'group' : 'groups'} • {totalObjects} {totalObjects === 1 ? 'object' : 'objects'})
                    </span>
                  </button>
                  <button
                    className="clear-button"
                    onClick={clearSelection}
                    disabled={selectedObjects.length === 0}
                  >
                    Clear
                  </button>
                  <div className="selected-count">
                    {selectedObjects.length} selected
                    {selectedObjects.length > 0 && (
                      <span className="points-display"> • +{selectedObjects.length * 10} pts</span>
                    )}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Warning Banner */}
          {!warningBannerDismissed && userObjects.length > 0 && (
            <WarningBanner onDismiss={handleDismissWarningBanner} />
          )}

          <div className="objects-list">
            {!userObjects || userObjects.length === 0 ? (
              <div className="empty-state">
                {/* Empty wallet - different for Toilet Mode */}
                                       {isToiletHolder || isDemoToiletMode ? (
                                       // TOILET MODE - Cozy Toilet
                                        <>
                                        <div className="empty-icon cozy-toilet-icon">
                                        <img 
                                        src="/cozy-toilet.png" 
                                        alt="Cozy Toilet" 
                                        style={{ width: '180px', height: 'auto', marginBottom: '1rem' }}
                                       />
                                     </div>
                                     <p className="toilet-empty-title">WALLET STATUS: ABSOLUTELY SPOTLESS 💎</p>
                                     <p className="toilet-empty-subtitle">Time to kick back with that Toilet Dust coffee ☕</p>
                                     <p className="toilet-empty-subtitle">Nothing left to purge. You've earned this break! 🔥</p>
                                     </>
                                ) : (
                                  // NORMAL MODE - Broom
                                   <>
                                   <div className="empty-icon">🧹</div>
                                   <p>No tokens to clean</p>
                                   {!isDemoMode && <p className="empty-subtitle">Your wallet is pristine!</p>}
                                   </>
                               )}
                {isDemoMode && <p className="empty-subtitle">Demo objects cleared! Toggle demo mode again to reset.</p>}
              </div>
            ) : (
              (() => {
                const tokensOnly = userObjects.filter(o => o?.enriched?.isCoin === true);
                const { groups, ungrouped } = groupTokens(tokensOnly);
                
                return (
                  <>
                    {/* RENDER GROUPS */}
                    {groups.map((group) => {
                      const isExpanded = expandedGroups.has(group.coinType);
                      const groupObjectIds = group.objects.map(obj => obj.data.objectId);
                      const selectedCount = groupObjectIds.filter(id => selectedObjects.includes(id)).length;
                      const allSelected = selectedCount === group.objectCount;
                      const someSelected = selectedCount > 0 && selectedCount < group.objectCount;
                      
                      // Calculate total balance
                      const formattedBalance = (group.totalBalance / Math.pow(10, group.decimals)).toFixed(group.decimals > 6 ? 6 : 2);
                      
                      return (
                        <div key={group.coinType} className="token-group">
                          {/* GROUP HEADER */}
                          <div className={`object-card-compact group-header ${allSelected ? 'selected' : ''} ${someSelected ? 'partial' : ''}`}>

                            {/* ▶ EXPAND ARROW - FAR LEFT */}
                            <button
                              className="expand-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedGroups(prev => {
                                  const newSet = new Set(prev);
                                  if (newSet.has(group.coinType)) {
                                    newSet.delete(group.coinType);
                                  } else {
                                    newSet.add(group.coinType);
                                  }
                                  return newSet;
                                });
                              }}
                            >
                              {isExpanded ? '▼' : '▶'}
                            </button>

                            {/* Checkbox */}
                            <div 
                              className={`object-checkbox-compact ${allSelected ? 'checked' : ''} ${someSelected ? 'indeterminate' : ''}`}
                              style={{ cursor: 'pointer', pointerEvents: 'all' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (allSelected || someSelected) {
                                  setSelectedObjects(prev => 
                                    prev.filter(id => !groupObjectIds.includes(id))
                                  );
                                } else {
                                  setSelectedObjects(prev => {
                                    const newSelection = [...prev];
                                    groupObjectIds.forEach(id => {
                                      if (!newSelection.includes(id)) newSelection.push(id);
                                    });
                                    return newSelection;
                                  });
                                }
                              }}
                            >
                              {allSelected && <span className="checkmark">✓</span>}
                              {someSelected && !allSelected && <span className="checkmark">-</span>}
                            </div>

                            {/* Token Icon */}
                            <div className="object-icon-container">
                              {group.iconUrl ? (
                                <img 
                                  src={group.iconUrl} 
                                  alt={group.name}
                                  className="object-icon-image"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextElementSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className="object-icon-emoji" style={{ display: group.iconUrl ? 'none' : 'flex' }}>
                                🪙
                              </div>
                            </div>

                            {/* Group Info - grows to fill space */}
                            <div className="object-info-group">
                              <div className="object-name-row">
                                <span className="object-name">{group.symbol}</span>
                              </div>
                              <div className="object-balance-row">
                                <span className="object-balance">{formattedBalance}</span>
                                <span className="group-count-inline">{group.objectCount} objects</span>
                              </div>
                            </div>

                            {/* 💵 USD VALUE - FAR RIGHT before ellipsis */}
                            <div className="object-value-horizontal">
                              <ObjectValueDisplay 
                                objectType={group.coinType}
                                balance={group.totalBalance}
                                decimals={group.decimals}
                                cachedPrice={tokenPrices[group.coinType]}
                              />
                            </div>

                            {/* 🎯 Ellipsis Menu */}
                            <ObjectMenu 
                              objectId={`group-${group.coinType}`}
                              objectType={group.coinType}
                              displayName={group.symbol}
                              menuType="groupHeader"
                            />
                          </div>

                          {/* EXPANDED INDIVIDUAL OBJECTS */}
                          {isExpanded && (
                            <div className="group-objects-expanded">
                              {group.objects.map((obj) => {
                                const isSelected = selectedObjects.includes(obj.data.objectId);
                                const isWhitelisted = obj.isWhitelisted || false;
                                const isNFT = obj.isNFT || false;
                                const category = categorizeObject(obj);
                                
                                return (
                                  <div
                                    key={obj.data.objectId}
                                    className={`object-card-compact individual-object ${isSelected ? 'selected' : ''} ${isWhitelisted ? 'whitelisted' : ''} ${isToiletHolder ? 'toilet-mode' : ''}`}
                                    onClick={() => !isWhitelisted && toggleObjectSelection(obj.data.objectId)}
                                    style={{ cursor: isWhitelisted ? 'not-allowed' : 'pointer', opacity: isWhitelisted ? 0.7 : 1 }}
                                  >
                                    {/* Phantom arrow spacer - keeps checkbox aligned with group headers */}
                                    <div className="expand-button-phantom" />

                                    {/* Checkbox - Custom Styled - DISABLED if whitelisted */}
                                    <div 
                                      className={`object-checkbox-compact ${isSelected ? 'checked' : ''} ${isWhitelisted ? 'disabled' : ''}`}
                                      style={{ 
                                        cursor: isWhitelisted ? 'not-allowed' : 'pointer', 
                                        pointerEvents: isWhitelisted ? 'none' : 'all',
                                        opacity: isWhitelisted ? 0.5 : 1
                                      }}
                                      onClick={(e) => {
                                        if (!isWhitelisted) {
                                          e.stopPropagation();
                                          toggleObjectSelection(obj.data.objectId);
                                        }
                                      }}
                                    >
                                      {isSelected && <span className="checkmark">✓</span>}
                                    </div>

                                    {/* Icon - 🪙 for tokens, NFT icon for NFTs */}
                                    <div className="object-icon-container">
                                      {obj?.enriched?.isCoin ? (
                                        <div className="object-icon-emoji">🪙</div>
                                      ) : (
                                        <img 
                                          src="/NFT.png" 
                                          alt="NFT"
                                          className="object-icon-emoji"
                                          style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                                        />
                                      )}
                                    </div>

                                    {/* ✅ FIX: Show token name + object ID like screenshot 4 */}
                                    <div className="object-info-group">
                                      <div className="object-name-row">
                                        <span className="object-name">{group.name}</span>
                                      </div>
                                      <div className="object-balance-row">
                                        <span className="object-id" style={{ fontSize: '0.75rem', opacity: 0.6 }}>
                                          {obj.data.objectId.slice(0, 8)}...{obj.data.objectId.slice(-6)}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Value Display */}
                                    <div className="object-value-horizontal">
                                      <ObjectValueDisplay 
                                        objectType={group.coinType}
                                        balance={obj.enriched?.rawBalance || obj.balance}
                                        decimals={group.decimals}
                                        mockUsdValue={obj.mockUsdValue}
                                        cachedPrice={tokenPrices[group.coinType]}
                                      />
                                    </div>

                                    {/* 🎯 Ellipsis Menu for Grouped Object - groupedObject type */}
                                    <ObjectMenu 
                                      objectId={obj.data.objectId}
                                      objectType={group.coinType}
                                      displayName={group.symbol}
                                      menuType="groupedObject"
                                    />

                                    {/* Category badge */}
                                    {!isNFT && (
                                      <div className={`category-badge ${category.type}`}>
                                        <span className="category-icon">{category.icon}</span>
                                        <span className="category-name">{category.name}</span>
                                      </div>
                                    )}

                                    {/* 🛡️ PROTECTION BADGE - Left side near name */}
                                    {isWhitelisted && (
                                                                                              <div className="protection-badge" style={{
                                                                                               position: 'absolute',
                                                                                               top: '8px',
                                                                                               right: '120px',  // ← Changed from left to right
                                                                                               background: 'rgba(34, 197, 94, 0.2)',
                                                                                               border: '1px solid rgba(34, 197, 94, 0.5)',
                                                                                               borderRadius: '6px',
                                                                                               padding: '2px 8px',
                                                                                               fontSize: '0.7rem',
                                                                                               fontWeight: '700',
                                                                                               color: '#22c55e',
                                                                                               display: 'flex',
                                                                                               alignItems: 'center',
                                                                                               gap: '4px',
                                                                                               zIndex: 10
                                                                                               }}>
                                                                                             <span>🛡️</span>
                                                                                             <span>PROTECTED</span>
                                                                                             </div>
                                                                                        )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* RENDER UNGROUPED OBJECTS */}
                    {ungrouped.map((obj) => {
                      const isSelected = selectedObjects.includes(obj.data.objectId);
                      const isWhitelisted = obj.isWhitelisted || false;
                      const isNFT = obj.isNFT || false;
                      const category = categorizeObject(obj);
                      const displayInfo = getObjectDisplayInfo(obj);
                      const objType = obj.data.type || obj.data.content?.type || 'Unknown';
                      
                      return (
                        <div
                          key={obj.data.objectId}
                          className={`object-card-compact solo-header ${isSelected ? 'selected' : ''} ${isWhitelisted ? 'whitelisted' : ''}`}
                          onClick={() => !isWhitelisted && toggleObjectSelection(obj.data.objectId)}
                          style={{ cursor: isWhitelisted ? 'not-allowed' : 'pointer', opacity: isWhitelisted ? 0.7 : 1, position: 'relative' }}
                        >
                          {/* Phantom arrow spacer - matches expand-button dimensions exactly */}
                          <div className="expand-button-phantom" />

                          {/* Checkbox - Custom Styled - DISABLED if whitelisted */}
                          <div 
                            className={`object-checkbox-compact ${isSelected ? 'checked' : ''} ${isWhitelisted ? 'disabled' : ''}`}
                            style={{ 
                              cursor: isWhitelisted ? 'not-allowed' : 'pointer', 
                              pointerEvents: isWhitelisted ? 'none' : 'all',
                              opacity: isWhitelisted ? 0.5 : 1
                            }}
                            onClick={(e) => {
                              if (!isWhitelisted) {
                                e.stopPropagation();
                                toggleObjectSelection(obj.data.objectId);
                              }
                            }}
                          >
                            {isSelected && <span className="checkmark">✓</span>}
                          </div>

                          {/* Icon - 🪙 for tokens, NFT icon for NFTs */}
                          <div className="object-icon-container">
                            {displayInfo.iconUrl ? (
                              <img 
                                src={displayInfo.iconUrl} 
                                alt={displayInfo.name}
                                className="object-icon-image"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextElementSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div 
                              className="object-icon-emoji"
                              style={{ display: displayInfo.iconUrl ? 'none' : 'flex' }}
                            >
                              {obj?.enriched?.isCoin ? (
                                <span style={{ fontSize: '24px', lineHeight: 1 }}>🪙</span>
                              ) : (
                                <img 
                                  src="/NFT.png" 
                                  alt="NFT"
                                  style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                                />
                              )}
                            </div>
                          </div>

                          {/* Object Info - grows to fill */}
                          <div className="object-info-group">
                            <div className="object-name-row">
                              <span className="object-name">{displayInfo.name}</span>
                            </div>
                            <div className="object-balance-row">
                              <span className="object-id" style={{ fontSize: '0.75rem', opacity: 0.6 }}>
                                {obj.data.objectId.slice(0, 8)}...{obj.data.objectId.slice(-6)}
                              </span>
                            </div>
                          </div>

                          {/* USD Value - far right */}
                          <div className="object-value-horizontal">
                            <ObjectValueDisplay 
                              objectType={objType}
                              balance={obj.enriched?.rawBalance || obj.data.content?.fields?.balance || obj.balance}
                              decimals={obj.enriched?.metadata?.decimals || obj.decimals || 9}
                              mockUsdValue={obj.mockUsdValue}
                              cachedPrice={obj?.enriched?.coinType ? tokenPrices[obj.enriched.coinType] : tokenPrices[objType]}
                            />
                          </div>

                          {/* 🎯 Ellipsis Menu - singleObject shows all options */}
                          <ObjectMenu 
                            objectId={obj.data.objectId}
                            objectType={objType}
                            displayName={displayInfo.name}
                            menuType="singleObject"
                          />

                          {/* Category badge area */}
                          {isNFT && !isWhitelisted && (
                            <div className="category-badge nft-warning" style={{
                              background: 'rgba(251, 191, 36, 0.2)',
                              borderColor: 'rgba(251, 191, 36, 0.5)'
                            }}>
                              <span className="category-icon">⚠️</span>
                              <span className="category-name" style={{ color: '#fbbf24' }}>NFT</span>
                            </div>
                          )}

                          {/* 🛡️ PROTECTION BADGE - Right side near price */}
                          {isWhitelisted && (
                            <div className="protection-badge" style={{
                              position: 'absolute',
                              top: '8px',
                              right: '120px',  // Changed from left to right
                              background: 'rgba(34, 197, 94, 0.2)',
                              border: '1px solid rgba(34, 197, 94, 0.5)',
                              borderRadius: '6px',
                              padding: '2px 8px',
                              fontSize: '0.7rem',
                              fontWeight: '700',
                              color: '#22c55e',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              zIndex: 10
                            }}>
                              <span>🛡️</span>
                              <span>PROTECTED</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                );
              })()
            )}
          </div>
          </>
        }
        distilleryContent={
          <div className="distillery-panel-wrapper">
            <Distillery 
              isActive={isDistilling}
              objectCount={selectedObjects.length}
              distillationsCount={purityScore.toFixed(2)}
              purityScore={purityScore}
              isDemoMode={isDemoMode}
              showStats={false}
              scale={1}
              isToiletMode={isToiletHolder}
              onOpenFlushFrenzy={() => setShowFlushFrenzy(true)}
            />
            
            {/* 🟤 PURGE BUTTON - MOVED TO BOTTOM OF DISTILLERY */}
            <div className="distillery-purge-bar">
              <motion.button
                className="purge-button-distillery"
                disabled={selectedObjects.length === 0 || isDistilling || (!isDemoMode && hasReachedLimit(isToiletHolder || isDemoToiletMode))}
                onClick={handlePurifyClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isDistilling ? (
                  <>
                    <Spinner />
                    <span>{isToiletHolder ? 'FLUSHING...' : 'PURGING...'}</span>
                  </>
                ) : (
                  <>
                    <span className="purify-icon">{isToiletHolder ? '🚽' : '🔥'}</span>
                    <span>
                      {isToiletHolder ? "LET'S F***ING FLUSH!" : 'PURGE THE FILTH'} ({selectedObjects.length})
                    </span>
                  </>
                )}
              </motion.button>
            </div>
          </div>
        }
      />
      {/* Footer - Compact Human + AI */}
      <footer className="app-footer-compact">
        <div className="footer-ai-badge">
          <span className="footer-ai-icon">🤖💙</span>
          <div className="footer-ai-text">
            <strong>Built by Human + AI</strong>
            <span>Direct collaboration • 2026</span>
          </div>
        </div>
        <div className="footer-links-simple">
          <a href="#about">About</a>
          <a href="https://github.com" target="_blank" rel="noopener noreferrer">GitHub</a>
          <a href="https://twitter.com" target="_blank" rel="noopener noreferrer">Twitter</a>
        </div>
        <div className="footer-copyright-simple">
          © 2026 Distil Protocol • Sui Blockchain • Human vision. AI execution.
        </div>
      </footer>

      {/* Toast Notifications */}
      <AnimatePresence>
        {toastMessage && (
          <AquaChronoToast
            message={toastMessage.message}
            type={toastMessage.type}
            onClose={() => setToastMessage(null)}
          />
        )}
      </AnimatePresence>

      {/* History Panel */}
      <AnimatePresence>
        {showHistory && (
          <HistoryPanel 
            isOpen={showHistory}
            onClose={() => setShowHistory(false)}
            walletAddress={currentAccount?.address || null}
            isDemoMode={isDemoMode}
          />
        )}
      </AnimatePresence>

      {/* Stats Panel */}
      <AnimatePresence>
        {showStats && (
          <StatsPanel 
            isOpen={showStats}
            onClose={() => setShowStats(false)}
            currentUserPurity={purityScore}
            walletAddress={currentAccount?.address || null}
          />
        )}
      </AnimatePresence>

      {/* Confirmation Modal */}
      <PurifyConfirmationModal
       isOpen={showConfirmation}
       onConfirm={handleConfirmPurify}
       onCancel={handleCancelPurify}
       objectCount={selectedObjects.length}
       selectedObjects={selectedObjects.map(id => 
       userObjects.find(obj => obj.data?.objectId === id)
       ).filter(Boolean)}
       isToiletMode={isToiletHolder}
       isDemoMode={isDemoMode}
      />

      {/* Whitelist Panel */}
      <AnimatePresence>
        {showWhitelist && (
          <WhitelistPanel 
            isOpen={showWhitelist}
            onClose={() => setShowWhitelist(false)}
            isAdmin={isAdmin}
            userObjects={userObjects}
            protectedAssets={protectedAssets}
            onUnwhitelist={handleUnwhitelist}
            recentlyWhitelisted={recentlyWhitelistedRef.current}
          />
        )}
      </AnimatePresence>

      {/* Toilet SpinWheel */}
      <ToiletSpinWheel
        isOpen={showSpinWheel}
        onClose={() => setShowSpinWheel(false)}
        flushCount={flushStreak}
        isWalletConnected={!!currentAccount?.address}
        onPrizeWon={(prize) => {
                   setWheelPrize(prize);
                   setWheelPrizeState(prize);
                   showToast(`🎰 Won: ${prize.bonus}!`, 'success');
                   // 🚽 Track SpinWheel win for Toilet badges (wallet-scoped)
                   trackToiletModeStats('spinWheelWin', 1, currentAccount?.address);
                   // Write prize purity to Supabase if it has a purity value
                   const spinPurity = prize.value || prize.purityBonus || prize.distilBonus || 0;
                   if (spinPurity > 0 && currentAccount?.address && !isDemoMode) {
                     recordGameReward({
                       walletAddress: currentAccount.address,
                       purityEarned: spinPurity,
                       gameType: 'spin_wheel',
                       gameScore: null,
                       isToiletMode: true,
                     }).catch(e => console.error('SpinWheel Supabase error:', e));
                   }
                   }}
        onPrizeClaimed={() => {
          showToast('Prize applied to next flush!', 'info');
        }}
      />

      {/* Help Guide */}
      <HelpGuide
        isOpen={showTutorial}
        onClose={() => setShowTutorial(false)}
      />

      {/* Whitepaper / Docs */}
      {showWhitepaper && (
        <Whitepaper onClose={() => setShowWhitepaper(false)} />
      )}

      {/* Referral Modal */}
      {showReferral && (
        <ReferralModal
          isOpen={showReferral}
          onClose={() => setShowReferral(false)}
          walletAddress={currentAccount?.address}
          isToiletMode={isToiletHolder}
        />
      )}

      {/* First Purge Warning Modal */}
      <FirstPurgeWarningModal
        isOpen={showFirstPurgeWarning}
        onClose={() => setShowFirstPurgeWarning(false)}
        onProceed={() => {
          setShowFirstPurgeWarning(false);
          setShowConfirmation(true);
        }}
        onGoToWhitelist={() => {
          setShowFirstPurgeWarning(false);
          setShowWhitelist(true);
        }}
        walletAddress={currentAccount?.address}
      />


      {/* Flush Frenzy Game */}
              <FlushFrenzy
                isOpen={showFlushFrenzy}
                onClose={() => setShowFlushFrenzy(false)}
                onGameComplete={handleGameComplete}
                isWalletConnected={!!currentAccount?.address}
                purityScore={purityScore}
                onSpendDistil={(amount) => {
                  const newScore = Math.max(0, purityScore - amount);
                  setPurityScore(newScore);
                  if (currentAccount?.address) {
                    localStorage.setItem(`distil-purity-score-${currentAccount.address}`, newScore.toString());
                  }
                  showToast(`🎟️ -${amount} $DISTIL — extra game unlocked!`, 'info');
                }}
              />

              {/* Scratch Off Game */}
              <ScratchOff
                isOpen={showScratchOff}
                onClose={() => setShowScratchOff(false)}
                isToiletMode={isToiletHolder || isDemoToiletMode}
                isWalletConnected={!!currentAccount?.address && !isDemoMode}
                onPrizeWon={handleScratchPrize}
              />

              {/* Toilet Warrior Activation Modal */}
              <ToiletWarriorActivation 
                isOpen={showWarriorActivation}
                onClose={() => setShowWarriorActivation(false)}
              />

              {/* 🔒 Toilet Mode Premium Modal — shown when no wallet connected */}
              {showToiletPremiumModal && ReactDOM.createPortal(
                <div
                  style={{
                    position: 'fixed', inset: 0, zIndex: 99999,
                    background: 'rgba(0,0,0,0.88)',
                    backdropFilter: 'blur(12px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '1rem',
                  }}
                  onClick={() => setShowToiletPremiumModal(false)}
                >
                  <div
                    style={{
                      background: 'linear-gradient(145deg, rgba(5,10,20,0.98), rgba(8,20,40,0.98))',
                      border: '2px solid rgba(255,215,0,0.5)',
                      borderRadius: '1.5rem',
                      padding: '2rem',
                      maxWidth: '420px',
                      width: '92%',
                      maxHeight: '92dvh',
                      overflowY: 'auto',
                      textAlign: 'center',
                      boxShadow: '0 0 60px rgba(255,215,0,0.3), 0 30px 80px rgba(0,0,0,0.9)',
                    }}
                    onClick={e => e.stopPropagation()}
                  >
                    {/* Icon */}
                    <div style={{ fontSize: '3.5rem', marginBottom: '0.75rem' }}>🚽</div>

                    {/* Title */}
                    <h2 style={{
                      fontSize: '1.4rem', fontWeight: 900, margin: '0 0 0.4rem',
                      fontFamily: "'Space Grotesk', sans-serif",
                      background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                    }}>
                      Toilet Mode
                    </h2>
                    <p style={{ color: 'rgba(0,212,200,0.9)', fontWeight: 700, margin: '0 0 1.25rem', fontSize: '0.95rem' }}>
                      Premium Feature — Toilet Warriors Only
                    </p>

                    {/* Benefits */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', marginBottom: '1.5rem', textAlign: 'left' }}>
                      {[
                        ['⚡', '+15% bonus $DISTIL earnings'],
                        ['🎰', 'SpinWheel, FlushFrenzy & ScratchOff'],
                        ['✨', 'Gold & Dark Cosmic visual themes'],
                        ['👑', 'Exclusive Toilet Warrior status'],
                        ['🎫', 'Hold 287,000+ $TOILET (~$10 USD)'],
                      ].map(([icon, text]) => (
                        <div key={text} style={{
                          display: 'flex', alignItems: 'center', gap: '0.6rem',
                          padding: '0.5rem 0.9rem',
                          background: 'rgba(255,215,0,0.06)',
                          border: '1px solid rgba(255,215,0,0.2)',
                          borderRadius: '0.6rem',
                        }}>
                          <span style={{ fontSize: '1.1rem' }}>{icon}</span>
                          <span style={{ color: '#FFD700', fontWeight: 700, fontSize: '0.86rem' }}>{text}</span>
                        </div>
                      ))}
                    </div>

                    {/* Buttons */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                      <a
                        href="https://flush.wtf"
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: 'block', width: '100%', padding: '0.85rem',
                          background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                          borderRadius: '0.75rem', color: '#000',
                          fontWeight: 900, fontSize: '1rem',
                          fontFamily: "'Space Grotesk', sans-serif",
                          textTransform: 'uppercase', letterSpacing: '0.06em',
                          textDecoration: 'none',
                          boxShadow: '0 6px 24px rgba(255,215,0,0.35)',
                        }}
                        onClick={() => setShowToiletPremiumModal(false)}
                      >
                        🛒 Get $TOILET on flush.wtf
                      </a>
                      <ConnectButton
                        style={{
                          width: '100%', padding: '0.75rem',
                          background: 'rgba(34,211,238,0.1)',
                          border: '1px solid rgba(34,211,238,0.4)',
                          borderRadius: '0.75rem', color: '#22d3ee',
                          fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer',
                        }}
                        onClick={() => setShowToiletPremiumModal(false)}
                      />
                      <button
                        onClick={() => setShowToiletPremiumModal(false)}
                        style={{
                          background: 'transparent', border: 'none',
                          color: 'rgba(148,163,184,0.6)', fontSize: '0.85rem',
                          cursor: 'pointer', padding: '0.25rem',
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>,
                document.body
              )}

      {/* 🎯 ADMIN PANEL - Only visible to admin wallet */}
      {currentAccount?.address === ADMIN_WALLET && (
        <button 
          className="admin-panel-toggle"
          onClick={() => setShowAdminPanel(!showAdminPanel)}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            padding: '12px 24px',
            background: showAdminPanel 
              ? '#ef4444' 
              : 'linear-gradient(135deg, #22d3ee 0%, #a855f7 100%)',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontWeight: '700',
            fontSize: '0.875rem',
            cursor: 'pointer',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            transition: 'all 0.2s',
          }}
        >
          {showAdminPanel ? '✖ Close Admin' : '🎯 Admin Panel'}
        </button>
      )}

      {/* 🚨 Quarantine Modal */}
      {showQuarantineModal && (
        <QuarantineModal
          quarantinedObjects={quarantinedObjects}
          onClose={() => setShowQuarantineModal(false)}
          onReturnItem={handleReturnFromQuarantine}
          isToiletMode={isToiletHolder}
        />
      )}

      {/* Protected Assets Modal */}
      {showProtectedModal && (
        <ProtectedAssetsModal
          protectedAssets={protectedAssets}
          filterStats={{}}
          onClose={() => setShowProtectedModal(false)}
          isToiletMode={isToiletHolder}
        />
      )}

      {/* Admin Panel Overlay */}
      {showAdminPanel && currentAccount?.address === ADMIN_WALLET && (
        <div 
          className="admin-panel-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.95)',
            zIndex: 9999,
            overflow: 'auto',
            padding: '20px',
          }}
          onClick={() => setShowAdminPanel(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <AdminPanel onClose={() => setShowAdminPanel(false)} />
          </div>
        </div>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <svg className="spinner" viewBox="0 0 50 50">
      <circle className="spinner-path" cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
    </svg>
  );
}

function AquaChronoToast({ message, type = 'success', onClose }) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const icons = {
    success: '✓',
    error: '✕',
    info: 'ℹ',
  };

  return (
    <motion.div
      className={`toast toast-${type}`}
      initial={{ x: 400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 400, opacity: 0 }}
      transition={{ type: 'spring', damping: 25 }}
    >
      <span className="toast-icon">{icons[type]}</span>
      <span className="toast-message">{message}</span>
      <button className="toast-close" onClick={onClose}>
        ✕
      </button>
    </motion.div>
  );
}

export default App;
